import { FlowCaptionCornerComponent } from '../../core/flow-caption-corner/flow-caption-corner.component';
import { FlowRecordingWidgetComponent } from './components/flow-recording-widget/flow-recording-widget.component';
import { FlowSessionIndicatorComponent } from './components/flow-session-indicator/flow-session-indicator.component';
import { WalkthroughHostComponent } from '../walkthrough/walkthrough-host.component';
import type {
  WorkspaceNavComponent,
  WorkspaceNavModel,
  WorkspaceNavSelection,
} from './components/workspace-nav/workspace-nav.component';
import {
  WORKSPACE_NAV_FLAG,
  navRouteForAnalytics,
  shellChromeSettled,
  workspaceNavIsStandard,
  workspaceNavActiveId,
  workspaceNavPlacement,
  workspaceNavReviewOverride,
} from './components/workspace-nav/workspace-nav.model';
import { playRouteEnter } from './components/workspace-nav/route-enter-motion';
import type { FrostActionId, FrostContext } from '../../shared/soft-frost-rail/soft-frost-rail.component';
import { FeatureFlagsService } from '../../core/services/feature-flags.service';
import { AskCompanionComponent } from '../ask-companion/ask-companion.component';
import { ASK_COMPANION_FLAG } from '../ask-companion/ask-companion.model';
import { AskPanelHostService } from '../../shared/ask/ask-panel-host';
import { environment } from '../../../environments/environment';
import { CoexistenceEntryComponent } from '../../shared/components/coexistence-entry/coexistence-entry.component';
import { DeskSlotChangeHostComponent } from '../../shared/components/desk-slot-change-host/desk-slot-change-host.component';
import { DOCUMENT, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, ViewContainerRef, computed, effect, inject, signal, untracked, viewChild, type AnimationCallbackEvent, type ComponentRef } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError, RouterLink, RouterOutlet } from '@angular/router';
import { filter, map, take } from 'rxjs';
import type { NewsItem } from '../../core/models/home-dashboard.model';
import { AdminTabsService } from '../../core/services/admin-tabs.service';
import { isAdminTabUrl } from '../../core/services/admin-tabs.utils';
import { FeatureFlagsViewerService } from '../../core/services/feature-flags-viewer.service';
import { AdminTabBarComponent } from '../settings/components/admin-tab-bar/admin-tab-bar.component';
import { AdminSearchService } from '../settings/components/admin-search-picker';
import { CmdkAiCommandsService } from './cmdk-ai-commands.service';
import { FlowCommandsService } from './flow-commands.service';
import { AskDeepLinkService } from '../../core/actions/ask-deep-link.service';
import { SurfaceMotionService, enterMotion, leaveMotion } from '../../core/motion';
import { isPreviewHarnessUrl, isTestHarnessSession } from './preview-harness';
import { sessionlessPreviewNow } from '../../core/preview-backend-gate';
import { AiTriggerRouterService } from './ai-trigger-router.service';
import { AIConversationService } from '../../core/services/ai-conversation.service';
import { AskPanelLaunchService } from '../../core/actions/ask-panel-launch.service';
import { deriveAskState, type AskState } from '../../shared/ai-widgets/ask-state';
import { PersonPanelComponent } from '../../shared/people/person-panel.component';
import { PersonPanelService } from '../../shared/people/person-panel.service';
import { ShellStateService, type RightPanel } from '../../core/services/shell-state.service';
import { SidePanelService } from '../../core/services/side-panel.service';
import { HotkeysService } from '../../core/services/hotkeys.service';
import { UpdaterService } from '../../core/services/updater.service';
import { DesktopTrayService } from '../../core/services/desktop-tray.service';
import { DesktopReminderService } from '../../core/services/desktop-reminder.service';
import { DesktopFlowService } from '../../core/services/desktop-flow.service';
import { CmdkComponent } from './components/cmdk/cmdk.component';
import {
  NAV_TRACE_MS,
  type NavTrace,
  findViewIconRect,
} from './components/nav-trace/nav-trace';
import { NavTraceComponent } from './components/nav-trace/nav-trace.component';
import { CmdkStateService } from './components/cmdk/services/cmdk-state.service';
import {
  PaperNotificationTriggerComponent,
  PaperToastStackComponent,
  PaperSearchPillComponent,
  PaperAiTriggerComponent,
  type PaperGlassBarVariant,
  PaperBannerComponent,
  PaperButtonComponent,
  PaperBottomNavComponent,
  type BottomNavItem,
} from '@ricoh-paper/angular';
import {
  PaperShellBarComponent,
  PaperShortcutRevealService,
  type PaperNavContext,
  type PaperNavContextSibling,
  type PaperNavHistoryEntry,
  type PaperNavLocation,
  type PaperNavTime,
  type PaperNavView,
  type PaperNavWorkspace,
  type PaperShellBarReveal,
} from '@ricoh-paper/angular/shell';
import { PopoverBandService } from '../../core/overlays/popover-band.service';
import { NotificationCenterService } from '../../core/services/notification-center.service';
import { RouteAnnouncerService } from '../../core/services/route-announcer.service';
import { ShortcutsDialogComponent } from './components/shortcuts-dialog/shortcuts-dialog.component';
import { SkipLinkDirective } from '../../shared/directives/skip-link.directive';
import { DesktopDragRegionDirective } from '../../shared/directives/desktop-drag-region.directive';
import { ProfileAvatarComponent } from './components/shared/profile-avatar.component';
import { ProfilePanelComponent } from './components/profile-panel/profile-panel.component';
import { AIPanelComponent } from './components/ai-panel/ai-panel.component';
import { FlowIntelligencePanelComponent } from './components/flow-intelligence-panel/flow-intelligence-panel.component';
import { InboxColumnComponent } from '../../shared/inbox/inbox-column.component';
import { InboxHostService } from '../../shared/inbox/inbox-host.service';
import { SidebarComponent } from '../viewer/components/sidebar/sidebar';
import { ViewerBootService } from '../../core/services/viewer-boot.service';
import { MapIntentPrefetchService } from '../../core/services/map-intent-prefetch.service';
import { ImmersiveCanvasService } from '../../core/services/immersive-canvas.service';
import { FlowSessionFocusService } from '../../core/services/flow-session-focus.service';
import { ArticleRegisterService } from '../../core/services/article-register.service';
import { ProfileDataService } from '../../core/services/profile-data.service';
import { ApprovalsService } from '../approvals/approvals.service';
import { AppUpdateService } from '../../core/services/app-update.service';
import { DelegationService } from '../../core/services/delegation.service';
import { homeRouteFrom } from '../../core/utils/home-link';
import { DelegationPillComponent } from './components/delegation-pill/delegation-pill.component';
import { MapCommandsService } from '../../core/services/map-commands.service';
import { MapEngineGate } from '../../core/map-engine/map-engine-gate.service';
import { SpacePanelService } from '../../core/services/space-panel.service';
import { afterFirstPaintSettled } from '../../core/utils/after-first-paint';
import { CommandCateringService } from '../../core/services/command-catering.service';
import { DivisionBrandingService } from '../../core/services/division-branding.service';
import { UserPreferencesService } from '../../core/services/user-preferences.service';
import { ProfileSettingsSyncService } from '../../core/services/profile-settings-sync.service';
import { DelegatePickerDialogComponent } from '../../shared/components/delegate-picker-dialog/delegate-picker-dialog.component';
import { QuickBookDialogComponent } from '../../shared/components/quick-book-dialog/quick-book-dialog.component';
import { PaperFeedbackWidgetComponent } from '../../shared/paper-app/paper-feedback-widget/paper-feedback-widget.component';
import { FirstRunComponent } from '../first-run/first-run.component';
import { AudioBarComponent } from './components/audio-bar/audio-bar.component';
import { NewsApiService } from '../../core/services/news-api.service';
import { ArticleDetailService } from '../../core/services/article-detail.service';
import { CriticalBannerComponent } from './components/critical-banner/critical-banner.component';
import { PaperConnectionBannerComponent } from '../../shared/paper-app/paper-connection-banner';
// IM-KEEP: Phase 3i — tenant-wide urgent building alerts banner.
import { PaperAlertsBannerComponent } from '../../shared/paper-app/paper-alerts-banner';
import {
  AlertBeaconComponent,
  type AlertBeaconToggle,
} from './components/alert-beacon';
import { BuildingAlertsService, type BuildingAlert } from '../../core/services/building-alerts.service';
import { ProjectDetailPanelComponent } from '../flow/components/project-detail-panel/project-detail-panel.component';
import { WhatsNewPanelComponent } from '../whats-new/whats-new-panel.component';
import {
  BOTTOM_NAV_WORKSPACES,
  COMMUNITY_DEFAULT_VIEW,
  COMMUNITY_ROOT_ROUTE,
  COMMUNITY_VIEWS,
  communityViewApplies,
  NAV_WORKSPACES,
  communityIdFromUrl,
  communityViewCommands,
  communityViewIdFromUrl,
  findWorkspace,
  isChromeTakeover,
  isViewActive,
  isWorkspaceVisible,
  personIdFromUrl,
  sectionAlertPresentation,
  sectionHidesBottomNav,
  sectionHidesShellBar,
  sectionUsesImmersiveCanvas,
  toContextNavViews,
  toNavViews,
  toNavWorkspace,
  toNavWorkspaces,
  viewsForUrl,
  workspaceRoute,
  type WorkspaceItem,
} from '../../core/constants/navigation';
// The Studio's level two, from the Studio's own registry rather than
// re-declared here. It is an Angular-free constants module — no component, no
// injectable, and its section surfaces are dynamic `import()` thunks, so
// naming it from the shell pulls in the list and not the Studio.
import {
  isStudioUrl,
  studioNavTarget,
  studioNavViewId,
  studioNavViews,
  studioRootForUrl,
} from '../content/studio/content-studio.registry';
import {
  NavHistoryService,
  type NavHistoryFrame,
} from '../../core/services/nav-history.service';
import {
  CommunitiesService,
  type Community,
} from '../../core/services/communities.service';
import { PublicProfileService } from '../../core/services/public-profile.service';
import { onShellEvent } from '../../core/tauri-runtime';
import {
  BmpDraftDockComponent,
  type DraftDockEntry,
} from '../viewer/components/book-meeting-panel/sections/bmp-draft-dock.component';
import { BmpDraftsLibraryComponent } from '../viewer/components/book-meeting-panel/sections/bmp-drafts-library.component';
import { BookMeetingPanelComponent } from '../viewer/components/book-meeting-panel/book-meeting-panel.component';
import { MeetingComposerHostComponent } from '../schedule-preview/host/meeting-composer-host.component';
import { MeetingPanelHostComponent } from '../schedule-preview/host/meeting-panel-host.component';
import { IssuePanelHostComponent } from '../issue-panel/issue-panel-host.component';
import { SpacePanelHostComponent } from '../viewer/components/space-panel-host/space-panel-host.component';
import { TaskDetailHostComponent } from '../viewer/components/task-detail-host/task-detail-host.component';
import { PushPrimerComponent } from './components/push-primer/push-primer.component';
import { AiReturnChipComponent } from './components/ai-return-chip/ai-return-chip.component';
import { AiLiveRegionComponent } from '../../shared/ai-widgets/ai-live-region.component';
import { MeetingDraftService } from '../../core/services/meeting-draft.service';
import { BookMeetingService } from '../../core/services/book-meeting.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LocaleService } from '../../core/services/locale.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../core/analytics/events';
import { LocationStateService } from '../../core/services/location-state.service';
import {
  MapContextChipService,
  type ContextChipSegment,
} from '../../core/services/map-context-chip.service';
import { LocationPickerComponent } from '../viewer/components/location-picker/location-picker';
import { getInitials } from '../../core/utils/text';
import { isScheduleRoute, SCHEDULE_ROUTE } from '../../core/utils/schedule-link';
import { Z_INDEX } from '../../core/constants/z-index';

/**
 * The workspace the shell falls back to when the URL resolves to no registry
 * entry at all. Home is first in the registry and the app's default route, so
 * this is a total function without a non-null assertion.
 */
const NAV_FALLBACK_WORKSPACE = NAV_WORKSPACES[0];

/**
 * A community's crumb / switcher tint.
 *
 * Paper's tone ladder is SEMANTIC (`success | warning | error | info | muted`)
 * and a community's `primaryColor` is a raw hex, so the record's own colour
 * cannot be projected here — an unrecognised tone resolves to `muted`
 * anyway. The distinction that survives the translation is the one §7.1
 * draws: a LOCATION or DIVISION community is the organisation's own and
 * carries the informational tone; a member-run CUSTOM one takes the neutral.
 */
function communityTone(community: Community | null): string | null {
  if (!community) return null;
  return community.type === 'CUSTOM' ? null : 'info';
}

/**
 * A sibling's secondary line, or nothing. Spread into the object so an empty
 * string never reaches the key at all — Paper renders the meta on a truthy
 * check, and an absent field is the honest way to say "no number".
 */
function metaLine(text: string): { meta?: string } {
  return text ? { meta: text } : {};
}

/**
 * App Shell — root layout component.
 *
 * Manages: router outlet, right panel slot (profile/AI), nav pill,
 * top-right controls, command palette overlay, page transitions.
 */
@Component({
  selector: 'im-app-shell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    WalkthroughHostComponent,
    DelegationPillComponent,
    FlowRecordingWidgetComponent,
    FlowCaptionCornerComponent,
    FlowSessionIndicatorComponent,
    PersonPanelComponent,
    CoexistenceEntryComponent,
    RouterOutlet,
    RouterLink,
    CmdkComponent,
    ShortcutsDialogComponent,
    SkipLinkDirective,
    DesktopDragRegionDirective,
    PaperNotificationTriggerComponent,
    PaperToastStackComponent,
    PaperSearchPillComponent,
    PaperAiTriggerComponent,
    PaperShellBarComponent,
    NavTraceComponent,
    ProfileAvatarComponent,
    ProfilePanelComponent,
    AIPanelComponent,
    FlowIntelligencePanelComponent,
    InboxColumnComponent,
    SidebarComponent,
    DelegatePickerDialogComponent,
    QuickBookDialogComponent,
    PaperFeedbackWidgetComponent,
    FirstRunComponent,
    AudioBarComponent,
    PaperBottomNavComponent,
    CriticalBannerComponent,
    AdminTabBarComponent,
    ProjectDetailPanelComponent,
    WhatsNewPanelComponent,
    PaperConnectionBannerComponent,
    PaperBannerComponent,
    PaperButtonComponent,
    BmpDraftDockComponent,
    BmpDraftsLibraryComponent,
    BookMeetingPanelComponent,
    MeetingComposerHostComponent,
    MeetingPanelHostComponent,
    IssuePanelHostComponent,
    SpacePanelHostComponent,
    TaskDetailHostComponent,
    DeskSlotChangeHostComponent,
    PushPrimerComponent,
    AiReturnChipComponent,
    AiLiveRegionComponent,
    // IM-KEEP: tenant-wide urgent building alerts (Phase 3i)
    PaperAlertsBannerComponent,
    AlertBeaconComponent,
    TranslocoPipe,
    LocationPickerComponent,
    // The Ask companion: deferred in the template, loaded only once `ask-dock` is on.
    AskCompanionComponent,
  ],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
  host: {
    '(document:keydown.escape)': 'onShellEscape($event)',
  },
})
export class AppShellComponent {
  protected readonly shell = inject(ShellStateService);
  private readonly engineGate = inject(MapEngineGate);
  /** True once the first paint has settled (or the page is the map): the engine-backed UI below may load its code. */
  private readonly engineSettled = signal(false);
  /** The map engine's UI (sidebar, location picker, space panel host) is @defer'd until this is true, so its code (and the engine chunk it brings) never competes with the first paint. */
  protected readonly engineWanted = computed(() => this.engineSettled() || this.engineGate.loaded());
  protected readonly spacePanels = inject(SpacePanelService);
  /** A colleague opened with no workspace panel of its own lies over this slot's tenant (PersonPanelService). */
  protected readonly personPanel = inject(PersonPanelService);
  private readonly sidePanel = inject(SidePanelService);
  protected readonly delegation = inject(DelegationService);

  // `columnOpen` — `SidePanelService.anyOpen`, mirrored onto `.app-shell` as
  // `.column-open` — is deleted with the two rules it gated: the nav bar's
  // notch and the page-level clearance. Nothing about the SHELL depends on a
  // column being up any more, because a column no longer moves the page. If a
  // future surface genuinely needs the fact, inject the service and read
  // `anyOpen` there; do not re-add a shell-wide class that invites a second
  // "get out of the way" rule to hang off it.
  protected readonly newsApi = inject(NewsApiService);
  protected readonly articleDetail = inject(ArticleDetailService);
  private readonly router = inject(Router);
  /** The address bar's route, which `goHome()` trusts before the router's first navigation ends. */
  private readonly location = inject(Location);
  private readonly analytics = inject(AnalyticsService);
  private readonly profileData = inject(ProfileDataService);
  /** Room approvals: the Approvals entry and its pending count, for anyone who can approve. */
  private readonly approvals = inject(ApprovalsService);
  private readonly hotkeys = inject(HotkeysService);
  private readonly updater = inject(UpdaterService);
  private readonly desktopTray = inject(DesktopTrayService);
  private readonly desktopReminders = inject(DesktopReminderService);
  private readonly desktopFlow = inject(DesktopFlowService);
  /** Drives the "update available" banner — true once a newer deploy is
   *  detected under this open tab. The banner's Refresh action reloads onto
   *  the fresh build (fresh `index.html` + chunk map). */
  protected readonly appUpdate = inject(AppUpdateService);

  /** Reload onto the freshly deployed build. */
  protected applyAppUpdate(): void {
    void this.appUpdate.applyUpdate();
  }

  /**
   * Observe the top banner stack and publish its rendered height as the
   * `--top-banner-stack-height` CSS variable on the document root. The shell
   * layout (`.app-shell`, `paper-glass-bar`, the below-glass nav pill) reads
   * that variable to offset itself beneath the banners — so one, two, or
   * three stacked banners all push content down by the right amount, and the
   * offset collapses to 0 when the stack is empty.
   */
  private setupBannerStackMeasurement(): void {
    const root = document.documentElement;
    let publishedHeight = -1;
    let resizeFrame: number | null = null;
    const publish = (height: number) => {
      const roundedHeight = Math.round(height);
      if (roundedHeight === publishedHeight) return;
      publishedHeight = roundedHeight;
      root.style.setProperty(
        '--top-banner-stack-height',
        `${roundedHeight}px`,
      );
      // The fixed Paper bar moves when this offset changes, but its content
      // box does not resize. Prompt its existing resize listener to republish
      // the measured --shell-sticky bottom used by every seated panel.
      if (resizeFrame !== null) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        window.dispatchEvent(new Event('resize'));
      });
    };
    let observer: ResizeObserver | null = null;

    effect(() => {
      const el = this.topBannerStack()?.nativeElement;
      observer?.disconnect();
      observer = null;
      if (!el) {
        publish(0);
        return;
      }
      // ResizeObserver fires whenever a banner shows/hides or wraps, keeping
      // the offset exact without polling. The stack height is independent of
      // the offset it drives, so there's no resize feedback loop.
      observer = new ResizeObserver(() => publish(el.getBoundingClientRect().height));
      observer.observe(el);
      publish(el.getBoundingClientRect().height);
    });

    this.destroyRef.onDestroy(() => {
      observer?.disconnect();
      if (resizeFrame !== null) cancelAnimationFrame(resizeFrame);
      // The press trace's own timeout — it writes a signal on a destroyed
      // component otherwise, which is a warning nobody can trace back to a
      // nav press that happened a second before a route change.
      if (this.navTraceTimer) clearTimeout(this.navTraceTimer);
    });
  }

  /** Fixed meeting surfaces must clear the visible operational alerts too. */
  private setupWorkspaceAlertMeasurement(): void {
    const root = this.document.documentElement;
    const viewport = this.document.defaultView;
    let publishedBottom = -1;
    effect((onCleanup) => {
      const element = this.urgentBannerArea()?.nativeElement;
      const floating = this.isMapSurface();
      const measure = () => {
        const bottom = element && !floating
          ? Math.max(0, Math.ceil(element.getBoundingClientRect().bottom))
          : 0;
        if (bottom === publishedBottom) return;
        publishedBottom = bottom;
        root.style.setProperty('--workspace-alert-bottom', `${bottom}px`);
      };
      const observer = new ResizeObserver(measure);
      if (element) observer.observe(element);
      viewport?.addEventListener('resize', measure);
      viewport?.addEventListener('scroll', measure, true);
      measure();
      onCleanup(() => {
        observer.disconnect();
        viewport?.removeEventListener('resize', measure);
        viewport?.removeEventListener('scroll', measure, true);
      });
    });
    this.destroyRef.onDestroy(() => root.style.removeProperty('--workspace-alert-bottom'));
  }

  /**
   * Track the narrow breakpoint for the shell bar's `reveal` mode. A media
   * query, not a resize listener: the browser evaluates it, and it matches
   * the `--bp-narrow` (768px) breakpoint the rest of the shell's CSS uses.
   */
  private trackCompactNav(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(max-width: 768px)');
    this.compactNav.set(query.matches);
    const onChange = (event: MediaQueryListEvent) => this.compactNav.set(event.matches);
    query.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => query.removeEventListener('change', onChange));
  }

  /**
   * Where the "Settings" affordances (bottom-nav pill, ⌘, menu) lead now that
   * the admin area lives at /admin and personal settings at /profile. Admins
   * (canManageSomething()) get the admin shell; everyone else gets their
   * personal profile — non-admins hitting /admin would be bounced home by
   * adminGuard. Mirrors the profile-panel's isAdmin predicate.
   */
  private settingsDestination(): string {
    return this.profileData.canManageSomething() ? '/admin' : '/profile';
  }
  private readonly destroyRef = inject(DestroyRef);
  /** Read-only, and only ever to measure a nav pill — see `nav-trace.ts`. */
  private readonly document = inject(DOCUMENT);
  /** The notifications feed behind the bell: its unread count, and the
   *  Inbox it opens in the right-hand slot (or Home's Inbox on Home). */
  protected readonly notificationCenter = inject(NotificationCenterService);
  /** Where the bell opens the Inbox, and the number it shows (Home's rail badge while Home is mounted). */
  protected readonly inboxHosts = inject(InboxHostService);
  // Boot the live notification feed (initial load + subscription + badge
  // poll). Idempotent; the shell only renders for authenticated users.
  private readonly _notificationFeed = (() => {
    this.notificationCenter.start();
    return true;
  })();
  /** Schedule V2 — global minimised-drafts store. The dock at the
   *  bottom of the shell template is bound to this. */
  protected readonly meetingDrafts = inject(MeetingDraftService);
  /** Used to hide the dock while the Book Meeting panel is on screen
   *  — a chip representing the same draft alongside the open panel is
   *  redundant and noisy. */
  private readonly bookMeeting = inject(BookMeetingService);
  protected readonly draftDockEntries = computed<DraftDockEntry[]>(() => {
    if (this.bookMeeting.visible()) return [];
    // The schedule redesign owns its draft chrome (creation-panel pill +
    // the My bookings Drafts section) — the shell dock would overlap its
    // panel footer and double-represent the same drafts.
    if (this.isSchedulePreview()) return [];
    return this.meetingDrafts.drafts().map(d => ({
      id: d.id,
      title: d.title,
      startMin: d.startMin,
      durationMin: d.durationMin,
      dateLabel: d.dateLabel,
    }));
  });

  /** Dock chip click → resume the draft in the SCHEDULE REDESIGN's
   *  creation panel (the only restore consumer — the legacy Book Meeting
   *  Panel no longer hydrates drafts). The pending request is a signal,
   *  so it survives the navigation and the schedule page consumes it on
   *  mount. Already there → the page's effect fires immediately. */
  protected onDraftRestore(id: string): void {
    this.meetingDrafts.requestRestore(id, 'dock');
    if (!isScheduleRoute(this.router.url)) {
      void this.router.navigate([SCHEDULE_ROUTE]);
    }
  }

  protected onDraftDiscard(id: string): void {
    this.meetingDrafts.remove(id);
  }

  /** Dock "View all" link — the room schedule's Drafts panel, the one place
   *  drafts live. (It opened the retiring panel's drafts library, a second
   *  list with its own Resume.) The request survives the navigation. */
  protected onShowDraftsLibrary(): void {
    this.meetingDrafts.requestPanel('dock');
    if (!isScheduleRoute(this.router.url)) {
      void this.router.navigate([SCHEDULE_ROUTE]);
    }
  }

  protected readonly modSymbol = this.hotkeys.modifierSymbol;

  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** Localised AI trigger label — null in Flow (icon-only), translated text everywhere else. */
  /**
   * The ⌥ keycap reveal, shared with the bar (`providedIn: 'root'`, so this
   * is the SAME instance the organism binds its own controls to).
   *
   * The Ask pill ships without its ⌘J keycap: it is the busiest control in
   * the cluster and the chip crowded it. The shortcut still fires — it is
   * HotkeysService's, not the pill's — and holding ⌥ brings the keycap back,
   * which is the app-wide discoverability mechanism (handover §2.8) rather
   * than a permanent hint on one button. The search pill keeps its keycap
   * (default `showKbd = true`); see the SCSS for why the two differ.
   *
   * The bar does this binding for the controls IT renders; these two are
   * projected into `[shell-bar-utility]` because the app's versions carry
   * behaviour Paper's defaults do not, so the binding has to be repeated
   * here.
   */
  protected readonly shortcutReveal = inject(PaperShortcutRevealService);

  /** Localised aria-label for the AI trigger button. */
  /** Render the admin tab strip inside .glass-center when in admin scope
   *  AND the user has tab mode enabled (toggled via Cmd-K). */
  protected readonly adminTabs = inject(AdminTabsService);
  private readonly featureFlags = inject(FeatureFlagsViewerService);
  private readonly adminSearch = inject(AdminSearchService);
  /** The command palette ⌘K reaches — see SPEC-command-k.md §1.5. */
  protected readonly cmdk = inject(CmdkStateService);
  private readonly aiTrigger = inject(AiTriggerRouterService);
  private readonly aiCommands = inject(CmdkAiCommandsService);
  private readonly flowCommands = inject(FlowCommandsService);
  private readonly aiConversation = inject(AIConversationService);

  /**
   * The Ask pill's existence. `aiPanelAllowed` (shell-state.service.ts) already
   * no-ops the open path for an AI-off viewer, so an ungated pill would be a
   * control that does nothing rather than a control that is absent — the
   * router's own `available()` is the single source both agree on.
   */
  protected readonly aiAvailable = computed(() => this.featureFlags.is('ai'));

  /**
   * What the pill's mark animates. Derived from the SAME function the panel
   * header uses, so the bar and the panel can never disagree about whether
   * Ask is thinking or replying.
   */
  protected readonly askState = computed<AskState>(() =>
    deriveAskState(this.aiConversation.isStreaming(), this.aiConversation.messages().at(-1)),
  );

  /** The pill docks the panel; see `openDockedPanel` for why it is not ⌘J's path. */
  protected onAskPillClicked(): void {
    const wasOpen = this.shell.rightPanel() === 'ai';
    this.aiTrigger.openDockedPanel();
    // The panel grows out of the pill — when it is the Workplace panel that
    // opened (admin and Flow route the pill to their own panels).
    if (!wasOpen && this.shell.rightPanel() === 'ai') {
      this.askLaunch.from(this.hostEl.nativeElement.querySelector('.ask-pill-anchor'));
    }
  }

  protected readonly askLaunch = inject(AskPanelLaunchService);
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);
  /** The docked Ask panel's arrival: a morph out of its trigger when hinted, else the column's own slide. */
  protected readonly aiPanelEnter = inject(SurfaceMotionService).enter('ask-panel', {
    surface: (host) => host.closest('.right-panel'),
    arrive: 'none',
    arrived: () => this.askLaunch.done(),
  });

  /**
   * True on the full-screen schedule redesign. That surface renders its OWN
   * "acting-as" strip, so the shell suppresses the global delegation banner
   * there to avoid a duplicate banner stealing the page's fixed header
   * (which hides the surface tabs). Delegation stays active — only the shell
   * banner is hidden for this one takeover route.
   *
   * `isScheduleRoute`, not `includes('/schedule')`: the previous-generation
   * page at `/schedule-legacy` is not a takeover and needs the shell banner.
   */
  protected readonly isSchedulePreview = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => isScheduleRoute(e.urlAfterRedirects)),
    ),
    { initialValue: isScheduleRoute(this.router.url) },
  );
  private readonly activeUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );
  /**
   * True on a route that serves the Home workspace — `/home`, `/welcome` and
   * the Home harnesses. All carry `data: { homeWorkspace: true }`; reading the
   * route rather than the URL keeps the answer with the route table. Louis's
   * navigation is drawn there whatever the `workspace-nav` flag says.
   */
  protected readonly isHomeWorkspaceIteration = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.routeIsHomeWorkspace()),
    ),
    { initialValue: this.routeIsHomeWorkspace() },
  );
  private routeIsHomeWorkspace(): boolean {
    return this.routeDataEnabled('homeWorkspace');
  }

  /** Help adopts the current navigation on cold loads, independent of the staged app-wide flag. */
  private readonly routeWorkspaceNav = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.routeDataEnabled('workspaceNav')),
    ),
    { initialValue: this.routeDataEnabled('workspaceNav') },
  );

  private routeDataEnabled(key: string): boolean {
    for (let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root; route; route = route.firstChild) {
      if (route.data[key] === true) return true;
    }
    return false;
  }

  // ── Louis's workspace navigation ────────────────────────────────────────
  //
  // `im-workspace-nav` (the soft-frost rail plus the app menu) replaces the
  // Paper bar on the Home workspace always, and on every employee route the
  // registry allows (`SECTION_CHROME.workspaceNav`) while the `workspace-nav`
  // PostHog flag is on. With the flag off nothing else changes. The rules are
  // pure and tested in `workspace-nav.model.ts`; this section only feeds them
  // and acts on what the nav hands back.

  /** The PostHog flags: `workspace-nav` is a trial flag, not a catalogue capability. */
  private readonly posthogFlags = inject(FeatureFlagsService);

  /** `?workspaceNav=1|0` on a dev build: review the nav without PostHog. The page's own query, read once. */
  private readonly workspaceNavOverride = workspaceNavReviewOverride(
    typeof window === 'undefined' ? '' : window.location.search,
    environment.production,
  );

  /** The Ask companion's flag (`ask-dock`, default off): its chunk loads once it is on. */
  protected readonly askCompanionFlag = computed(() => this.posthogFlags.flagEnabled(ASK_COMPANION_FLAG));

  /** The `workspace-nav` flag, after the review override. Default off. */
  protected readonly workspaceNavFlag = computed(
    () => this.workspaceNavOverride ?? this.posthogFlags.flagEnabled(WORKSPACE_NAV_FLAG),
  );

  /** Where the workspace nav sits on this route, or null for the Paper bar (or nothing). */
  protected readonly workspaceNavPlacement = computed(() =>
    workspaceNavPlacement({
      homeWorkspaceRoute: this.isHomeWorkspaceIteration(),
      flagOn: this.routeWorkspaceNav() || this.workspaceNavFlag(),
      section: this.shell.currentSection() ?? 'home',
      adminScope: this.inAdminScope(),
      chromeTakeover: this.chromeTakeover(),
    }),
  );

  /**
   * COLD LOAD: NO BAR UNTIL WE KNOW WHICH BAR. The shell is built before the
   * router's first navigation ends and before PostHog answers, so the route
   * read as "not Home" and the flag as off: the Paper bar painted, then gave
   * way to the nav (whose chunk loads on demand), and a nav drawn for the
   * first guess was replaced once the route settled. Until both are known
   * neither is drawn; a press on the first nav's More button could open a
   * menu whose trigger was then swapped out (workspace-nav.spec.ts:169).
   */
  private readonly routed = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      take(1),
      map(() => true),
    ),
    { initialValue: this.router.navigated },
  );
  /** PostHog may never answer (no key, a blocker): stop waiting for the flag after this long. */
  private static readonly NAV_FLAG_WAIT_MS = 1500;
  private readonly navFlagWaitOver = signal(false);
  /** The `workspace-nav` flag can be relied on: overridden, answered, or no longer waited for. */
  private readonly navFlagKnown = computed(
    () => this.routeWorkspaceNav() || this.workspaceNavOverride !== null || this.posthogFlags.ready() || this.navFlagWaitOver(),
  );
  /** Route known, and the flag known wherever it decides (on Home and Flow the nav is drawn regardless). */
  protected readonly chromeSettled = computed(() =>
    shellChromeSettled({
      routed: this.routed(),
      standardWorkspaceNav: workspaceNavIsStandard(this.isHomeWorkspaceIteration(), this.shell.currentSection() ?? 'home'),
      flagKnown: this.navFlagKnown(),
    }),
  );

  /**
   * A live Flow session on its own page holds the screen (flow-session-v2): the session frame stands in for the
   * app's navigation, and carries its own way back. Runtime, so not registry data (`FlowSessionFocusService`).
   */
  private readonly sessionFocus = inject(FlowSessionFocusService);

  /** Whether the workspace nav is drawn on this route (in place of the Paper bar). */
  protected readonly workspaceNavShown = computed(() => this.chromeSettled() && this.workspaceNavPlacement() !== null && !this.sessionFocus.barHidden());

  /** Which panel Ask opens in: the workspace you are in (Home's tools), else the shell's docked panel. */
  private readonly askHosts = inject(AskPanelHostService);
  /** The page has its own Ask (an `ownsAsk` host: the Spotlight writer). The bar's Ask pill steps aside: one Ask. */
  protected readonly pageOwnsAsk = computed(() => this.askHosts.active().ownsAsk === true);

  /**
   * Ask is the nav's current item while it is open from the side — in
   * whichever host it opened in, unless that host keeps the nav on its own
   * workspace (the Flow planner). The host contract is the one source; the
   * nav keeps no second idea of whether Ask is open.
   */
  protected readonly askIsCurrent = computed(() => this.askHosts.navCurrent());

  /** The nav's current item: the route's workspace, or Ask. */
  protected readonly workspaceNavActiveId = computed(() =>
    workspaceNavActiveId(this.shell.currentSection() ?? 'home', this.askIsCurrent()),
  );

  /**
   * The place the page is inside, at the head of level two: a community or a
   * colleague, with the way back. Back retraces the path when there is one; a
   * community without one goes up to the directory. Content Studio is a
   * workspace in its own right — its chip already names it — so it gets no
   * crumb, only its sections.
   */
  private readonly workspaceNavContext = computed<FrostContext | null>(() => {
    const context = this.navContext();
    if (!context || (context.kind !== 'community' && context.kind !== 'person')) return null;
    this.locale.locale(); // track for reactivity
    const previous = this.navHistory().at(-1);
    if (previous) {
      return {
        label: context.label,
        action: 'back',
        actionLabel: this.transloco.translate('shell.workspace_nav.back_to', { place: previous.label }),
      };
    }
    if (context.kind === 'community') {
      return { label: context.label, action: 'up', actionLabel: context.rootLabel };
    }
    return { label: context.label, action: null, actionLabel: '' };
  });

  /** A page has scrolled beneath the workspace nav: its plate shows. See `watchNavScroll`. */
  private readonly navScrolled = signal(false);

  /**
   * Everything the nav needs, in one object. Null while it is not drawn.
   *
   * The items are `navWorkspaces()` — the same capability- and grant-filtered
   * list the Paper bar renders, with the workspace you are in railed — and the
   * views are the bar's own level two (a context's views win, as in Paper).
   */
  protected readonly workspaceNavModel = computed<WorkspaceNavModel | null>(() => {
    const placement = this.workspaceNavPlacement();
    if (!placement) return null;
    this.locale.locale(); // track for reactivity
    const section = this.shell.currentSection() ?? 'home';
    const site = this.chip.barSite();
    const time = this.chipTime();
    // Level two belongs to a workspace (or a place inside one). Louis's Home
    // has none — search keeps the centre, as he drew it, and Bookings and
    // Services stay in the app menu's Views group — and neither has a section
    // that is not a workspace (`/ai`, personal settings), whose fallback would
    // otherwise borrow Home's.
    const context = this.navContext();
    const views = this.isHomeWorkspaceIteration() || this.routeWorkspaceNav() || (!context && !findWorkspace(section))
      ? []
      : (context?.views ?? this.navViews());
    return {
      placement,
      surface: section,
      route: navRouteForAnalytics(this.activeUrl() ?? ''),
      workspaces: this.navWorkspaces(),
      activeId: this.workspaceNavActiveId(),
      viewsOf: workspaceNavActiveId(section, false),
      askLabel: this.aiAvailable() ? this.transloco.translate('shell.workspace_nav.ask') : null,
      views: views.map(view => ({ id: view.id, label: view.label })),
      activeViewId: this.activeNavViewId(),
      context: this.workspaceNavContext(),
      organisationMark: this.tenantMark(),
      organisationLogo: this.tenantLogo(),
      organisationName: this.tenantName(),
      locationLabel: site.short ?? site.label,
      locationDetail: this.isMapSurface() ? (this.chip.location()?.detail ?? null) : null,
      time: time
        ? { label: time.label, live: time.live, ...(time.dayLabel ? { dayLabel: time.dayLabel } : {}) }
        : null,
      scrolled: placement === 'page' && this.navScrolled(),
      locationOpen: this.chip.openSegment() === 'location',
      timeOpen: this.chip.openSegment() === 'time',
    };
  });

  /** Where the workspace nav goes; present only while it is drawn. */
  private readonly workspaceNavHost = viewChild('workspaceNavHost', { read: ViewContainerRef });
  private workspaceNav: ComponentRef<WorkspaceNavComponent> | null = null;

  /**
   * Loads the workspace nav when its slot appears and keeps its model in step.
   * A plain dynamic import rather than `@defer`, whose runtime would itself
   * join the initial bundle: with the flag off the nav is drawn only on the
   * Home workspace, so it, its rail and its icons stay out of the initial
   * bundle. Dropped with the slot.
   */
  private mountWorkspaceNavWhenShown(): void {
    effect(() => {
      const host = this.workspaceNavHost();
      const model = this.workspaceNavModel();
      untracked(() => {
        if (!host) {
          this.workspaceNav = null;
          return;
        }
        if (this.workspaceNav && this.workspaceNav.hostView === host.get(0)) {
          this.workspaceNav.setInput('model', model);
          return;
        }
        void this.mountWorkspaceNav(host);
      });
    });
  }

  private async mountWorkspaceNav(host: ViewContainerRef): Promise<void> {
    const { WorkspaceNavComponent } = await import('./components/workspace-nav/workspace-nav.component');
    // The slot went (or was replaced) while the chunk loaded.
    if (this.workspaceNavHost() !== host || host.length) return;
    const ref = host.createComponent(WorkspaceNavComponent);
    ref.setInput('model', this.workspaceNavModel());
    ref.instance.workspaceSelected.subscribe((selection) => this.onWorkspaceNavWorkspace(selection));
    ref.instance.viewSelected.subscribe((selection) => this.onWorkspaceNavView(selection));
    ref.instance.action.subscribe((event) => this.onWorkspaceNavAction(event));
    ref.instance.navigationStarted.subscribe((origin) => this.noteNavLaunch(origin));
    this.workspaceNav = ref;
  }

  /** The control that opened the profile menu from the nav — the avatar (the mark is Home since 4 Oct 2026). */
  private workspaceNavProfileTrigger: HTMLElement | null = null;

  protected onWorkspaceNavAction(event: { id: FrostActionId; trigger: HTMLElement }): void {
    switch (event.id) {
      case 'search': this.openCommandPalette(event.trigger); break;
      case 'location': this.onChipSegment('location', event.trigger); break;
      case 'time': this.onChipSegment('time', event.trigger); break;
      // The bell opens the Inbox: in the page's own panel where it has one (Home), else the floating column.
      case 'notifications': this.inboxHosts.toggle('bell', event.trigger); break;
      // The division's mark, top left, is Home (Nath, 4 Oct 2026); the profile is the avatar's alone.
      case 'home': this.goHome(); break;
      case 'profile':
        // The menu grows out of the avatar that opened it.
        this.workspaceNavProfileTrigger = event.trigger;
        this.shell.toggleProfilePanel();
        break;
      case 'ask': this.onWorkspaceNavAsk(event.trigger); break;
      case 'back': this.onWorkspaceNavBack(); break;
      case 'more': break; // the nav opens its own app menu
    }
  }

  /**
   * The Ask chip is a door like every other (`AiTriggerRouterService`): Ask in
   * the current workspace's panel, growing out of the chip, put away on a
   * second press; a new session when it opens; nothing on `/ai`, where Ask is
   * the page; Flow's own assistant on Flow. One owner, so the chip fires the
   * same `SHELL_AI_TRIGGER_SELECTED` as the rest, with `source: 'rail'`.
   */
  private onWorkspaceNavAsk(trigger: HTMLElement): void {
    this.aiTrigger.route('rail', trigger);
  }

  /** The crumb's arrow: back along the path, or up to the communities when there is no path. */
  private onWorkspaceNavBack(): void {
    if (this.navHistory().length > 0) {
      this.onBackRequested();
      return;
    }
    this.onContextRootSelected();
  }

  protected onWorkspaceNavWorkspace(selection: WorkspaceNavSelection): void {
    const workspace = this.navWorkspaces().find((candidate) => candidate.id === selection.id);
    if (!workspace) return;
    if (workspace.id === 'home') {
      this.goHome();
      return;
    }
    this.onWorkspaceSelected(workspace);
  }

  /**
   * Home, from its chip or from the division's mark. A no-auth harness stays
   * in the harnesses (the live-shape preview keeps its own route); everywhere
   * else Home is `/home`, the real one.
   *
   * Read off the address bar, not the last finished navigation: until the
   * first one ends the router still says `/`, so a press in that second sent
   * a harness to `/home`, and `/home`'s auth guard sent it on to sign-in.
   */
  private goHome(): void {
    void this.router.navigateByUrl(homeRouteFrom(this.location.path() || this.activeUrl() || ''));
  }

  protected onWorkspaceNavView(selection: WorkspaceNavSelection): void {
    const views = this.navContext()?.views ?? this.navViews();
    const view = views.find((candidate) => candidate.id === selection.id);
    if (view) this.onNavViewSelected(view);
  }

  // ── Every surface opens from what launched it ───────────────────────────
  //
  // A page reached from the nav grows out of the control that opened it; any
  // other page rises into place. The old page is gone before the new one is
  // drawn (the router swaps them in one step), so no frame shows two pages'
  // text. Under the workspace nav this replaces the CSS page-enter, whose
  // fill left a transform on the page for good.

  /**
   * The control the next page grows out of, from the press until that
   * navigation settles: the outlet's `activate` (which the router fires before
   * `NavigationEnd`) takes it, and the navigation's end drops it either way —
   * so a press that changed only a query, or went nowhere, never lends its
   * chip to a later page.
   */
  private navLaunch: HTMLElement | null = null;
  private readonly routerOutlet = viewChild(RouterOutlet, { read: ElementRef });

  private noteNavLaunch(origin: HTMLElement): void {
    this.navLaunch = origin;
  }

  protected onRouteActivated(): void {
    const launch = this.navLaunch;
    this.navLaunch = null;
    if (!this.workspaceNavShown()) return;
    const page = (this.routerOutlet()?.nativeElement as HTMLElement | undefined)?.nextElementSibling;
    if (!(page instanceof HTMLElement)) return;
    playRouteEnter(page, launch?.isConnected ? launch : null);
  }

  /**
   * The plate under the workspace nav shows once a page's content scrolls
   * beneath it — Home's canvas, a settings page — and goes when that scroller
   * is back at the top. A scroller that starts below the nav (the schedule's
   * grid, a map panel) never passes under it, so it never plates the bar.
   * Floating over a canvas there is no plate at all.
   */
  private watchNavScroll(): void {
    effect((onCleanup) => {
      const placement = this.workspaceNavPlacement();
      this.activeUrl(); // a new page starts at the top
      untracked(() => this.navScrolled.set(false));
      if (placement !== 'page') return;
      let owner: Element | null = null;
      let pending: Element | null = null;
      let frame = 0;
      const evaluate = () => {
        frame = 0;
        const target = pending;
        pending = null;
        const nav = this.workspaceNav?.location.nativeElement as HTMLElement | undefined;
        if (!target || !nav) return;
        const navBottom = nav.getBoundingClientRect().bottom;
        const under = target.getBoundingClientRect().top < navBottom - 1;
        if (under && target.scrollTop > 8) {
          owner = target;
          this.navScrolled.set(true);
        } else if (target === owner) {
          owner = null;
          this.navScrolled.set(false);
        }
      };
      const onScroll = (event: Event) => {
        const target = event.target === this.document ? this.document.scrollingElement : event.target;
        if (!(target instanceof Element)) return;
        pending = target;
        if (!frame) frame = requestAnimationFrame(evaluate);
      };
      this.document.addEventListener('scroll', onScroll, { capture: true, passive: true });
      onCleanup(() => {
        this.document.removeEventListener('scroll', onScroll, { capture: true });
        if (frame) cancelAnimationFrame(frame);
      });
    });
  }
  /**
   * True on a route that renders its OWN complete chrome over the shell (see
   * `CHROME_TAKEOVERS` in the navigation registry). The shell renders NEITHER
   * the bar nor the dock there — not because they would look wrong, but
   * because a second copy of each is a live duplicate: two avatars toggling
   * one `ShellStateService` signal, two notification trigger/panel pairs on
   * one `NotificationCenterService`, and both sets focusable underneath an
   * opaque overlay.
   *
   * Prefix-keyed rather than section-keyed on purpose: ownership is a property
   * of ONE route, and its siblings in the same section (`/schedule`, where the
   * view track's tabs land) need the shell's chrome.
   */
  protected readonly chromeTakeover = computed(() =>
    isChromeTakeover(this.activeUrl() ?? ''),
  );

  /**
   * Whether the shell renders `<paper-shell-bar>`. Both halves are registry
   * data: the section's own decision (`SECTION_CHROME`) and whether the route
   * has taken chrome ownership. The shell holds no list of section names.
   * Where Louis's workspace nav is drawn it stands in for the bar.
   */
  protected readonly showShellBar = computed(
    () => this.chromeSettled() && !sectionHidesShellBar(this.shell.currentSection() ?? 'home')
      && !this.chromeTakeover()
      && !this.workspaceNavShown()
      && !this.sessionFocus.barHidden(),
  );

  /**
   * Whether the shell renders `<paper-bottom-nav>` — same two sources.
   *
   * Never beside the workspace nav, at any width. On a phone Louis's bar is
   * the one navigation: the workspace you are in, its chevron opening every
   * destination, search, the site, notifications and you — as Home has always
   * drawn it — and the page keeps the 64px the dock took.
   */
  protected readonly showBottomNav = computed(
    () => this.chromeSettled() && !sectionHidesBottomNav(this.shell.currentSection() ?? 'home')
      && !this.chromeTakeover()
      && !this.workspaceNavShown()
      && !this.sessionFocus.active(),
  );

  /**
   * The bar is floating over a full-bleed canvas (Map, World) — design
   * handover §3.1. Registry data, exactly like the suppression above: the
   * shell holds no section list. Drives the plate-less treatment in
   * `app-shell.component.scss`, and nothing else — the "no view track" half of
   * §3.1 falls out of those workspaces declaring no views.
   */
  protected readonly immersiveCanvas = computed(
    () => sectionUsesImmersiveCanvas(this.shell.currentSection() ?? 'home'),
  );

  /**
   * An immersive section whose canvas has NOT painted yet — the bar keeps its
   * plate for as long as this is true.
   *
   * §3.1's treatment takes the bar's plate away AND makes the controls inside
   * its clusters transparent, both so a rendered canvas reads through the
   * chrome. Over an EMPTY canvas that leaves nothing: measured on the map's
   * cold boot, the bar was a row of hairlines on flat `--bg` for the first
   * three seconds and then vanished entirely behind the map-view loading
   * overlay. The bar was never unmounted — `showShellBar()` is registry data
   * and correct throughout — it was made invisible, twice over.
   *
   * WHY THIS IS NOT REGISTRY DATA. Which sections MAY go plate-less is a
   * static property of a section and lives in `SECTION_CHROME`. Whether the
   * canvas is painted is runtime state owned by whichever surface draws it,
   * so it arrives through `ImmersiveCanvasService` — section-agnostic, and
   * defaulting to painted so the globe is untouched.
   */
  private readonly immersive = inject(ImmersiveCanvasService);
  /** The article writer/reader's paper, when one of them is mounted. See
   *  `chromePaper` below for why the registry alone cannot do this. */
  private readonly articleRegister = inject(ArticleRegisterService);

  protected readonly canvasPending = computed(
    () => this.immersiveCanvas() && !this.immersive.painted(),
  );

  /**
   * The paper the shell's own floating chrome prints on — `null` everywhere
   * except under an article surface, which re-themes its routed host and
   * cannot reach the bar, the dock or the feedback widget from there (they are
   * siblings of `<router-outlet>`, not descendants). Bound as
   * `[attr.data-theme]`, so a `null` REMOVES the attribute and the chrome
   * inherits `<html>`'s paper.
   *
   * An article surface re-themes at runtime (the reader's sub-nav carries a
   * light/dark toggle, and `auto` follows a viewer who changes the app theme
   * underneath it). Without this the writer pins Midnight on its canvas while
   * the bar above it stays cream. Games used to be the static case beside it
   * (`SectionChrome.chromePaper`); it follows the viewer's paper now, so the
   * registry field went with it (5 Oct 2026).
   */
  protected readonly chromePaper = computed(() => this.articleRegister.chromePaper());
  private readonly inAdminScope = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => isAdminTabUrl(e.urlAfterRedirects)),
    ),
    { initialValue: isAdminTabUrl(this.router.url) },
  );
  protected readonly showAdminTabs = computed(
    () =>
      this.inAdminScope() &&
      this.adminTabs.enabled() &&
      this.featureFlags.isEnabled('policyV2AdminTabsEnabled')(),
  );

  /** Resolved viewer capability set — drives the shell bar's workspace +
   *  view lists and the bottom-nav gating below. The set signal (not `is()`)
   *  so the chrome re-filters reactively once the flags load at boot. Fails
   *  CLOSED: gated destinations stay hidden until the load resolves. */
  private readonly enabledFlags = this.featureFlags.enabled();

  /** Whether the user is currently inside the admin route tree
   *  (`/settings/{anything}` and not a personal-settings sub-route).
   *  Drives the routing decision for ⌘K and the AI trigger so a single
   *  global affordance serves both contexts. */
  protected readonly isInAdmin = this.inAdminScope;

  /** Template ref to the search pill wrapper — used as the anchor for
   *  the admin search picker when ⌘K fires from a hotkey (no DOM event
   *  to grab a target from). */
  protected readonly searchPillAnchor = viewChild<ElementRef<HTMLElement>>('searchPillAnchor');

  /** The fixed container holding every top status banner. Its measured
   *  height is published as --top-banner-stack-height so the shell chrome
   *  offsets beneath it (see setupBannerStackMeasurement). */
  private readonly topBannerStack = viewChild<ElementRef<HTMLElement>>('topBannerStack');
  private readonly urgentBannerArea = viewChild<ElementRef<HTMLElement>>('urgentBannerArea');

  /** The right-panel slot container — the focus target for §5.5. */
  private readonly rightPanelSlot = viewChild<ElementRef<HTMLElement>>('rightPanelSlot');

  /** Control that opened the slot, so Escape can hand focus back to it. */
  private slotTrigger: HTMLElement | null = null;

  /**
   * §5.5 for the shell slot — focus moves in on open, and goes back to the
   * control that opened it on close.
   *
   * The trigger is `document.activeElement` at the moment the slot's tenant
   * changes. That is deliberately not a parameter threaded through
   * `ShellStateService`: the slot is opened from the avatar, the bell, the AI
   * pill, ⌘J, the command palette, a widget CTA and a deep link, and only one
   * of those has an element to pass. Reading the focused element covers every
   * path that HAS a trigger and correctly finds none for the ones that do not.
   *
   * Focus lands on the CONTAINER, not the first control inside it. A screen
   * reader then announces the panel before its contents, and a keyboard user's
   * next Tab is the panel's first control rather than its second.
   */
  private manageSlotFocus(): void {
    // Keyed on the TENANT, not on "is anything open". The slot is
    // single-tenant but not single-transition: pressing the avatar while the
    // notification centre is up swaps `notifications` → `profile` without the
    // boolean ever going false, and a boolean guard would leave focus on a
    // control that has just been destroyed by the @switch.
    let previous: RightPanel = 'none';
    effect(() => {
      // `slotTenant()`, not `rightPanel()`. The profile menu is a popover
      // now and the BAND owns its focus (in on open, back to the avatar on
      // close, `PopoverBandService`). Reading the raw signal here would make
      // this effect fire for a surface that never enters the slot, focus an
      // empty `<aside>`, and then fight the band over the return.
      const tenant = this.slotTenant();
      if (tenant === previous) return;
      const wasClosed = previous === 'none';
      previous = tenant;

      if (tenant !== 'none') {
        // Only capture a trigger on a genuine open. On a swap the focused
        // element is inside the panel that is being replaced, and remembering
        // that would hand focus to a detached node on close.
        if (wasClosed) {
          const active = document.activeElement;
          this.slotTrigger = active instanceof HTMLElement && active !== document.body
            ? active
            : null;
        }
        // Deferred: the @switch case has not rendered on this tick.
        queueMicrotask(() => {
          if (this.shell.rightPanel() === 'none') return;
          this.rightPanelSlot()?.nativeElement.focus({ preventScroll: true });
        });
        return;
      }

      const trigger = this.slotTrigger;
      this.slotTrigger = null;
      if (!trigger || !document.contains(trigger)) return;
      queueMicrotask(() => {
        if (document.contains(trigger)) trigger.focus({ preventScroll: true });
      });
    });
  }

  /**
   * §14 — the shell slot is a REGISTERED COLUMN, like every other.
   *
   * It used to be a flex sibling of `.main-surface` that yielded width by
   * shrinking the row, while the services request panel and the map's
   * inspectors yielded through `SidePanelService`. Two mechanisms for one
   * idea, and they could disagree about whether the canvas had moved. NEITHER
   * survives: the slot is `position: fixed`, it floats over the page like
   * every other column, and no page content moves for any of them.
   *
   * Registration still matters — Escape, §5.4's surface scope and §5.5's
   * focus return are all keyed to the registry. It is only the geometry that
   * the registry no longer drives.
   *
   * `surfaceScoped: false` — profile and the AI companions follow you between
   * workspaces by design, and the notification centre already self-closes on a
   * section change inside `ShellStateService`. Registering it as scoped here
   * would be a second implementation of that same rule.
   *
   * No `trigger` is handed over: `manageSlotFocus()` above owns §5.5 for this
   * slot and already restores focus. Giving the registry a trigger as well
   * would return focus twice, from two places, on every close.
   */
  // ── ONE DERIVATION OF "WHICH FAMILY IS OPEN" (§14.5a) ───────────────────
  //
  // `ShellStateService.rightPanel()` is a five-way signal that predates the
  // family split, so it still names `'profile'` alongside three columns.
  // Every consumer that used to read it now reads ONE of the three
  // derivations below, and none of them re-states the condition:
  //
  //   slotTenant()         what the `<aside>`'s @switch may render
  //   columnOpen()         is a COLUMN up — the notch, the registry, Escape
  //   profilePopoverOpen() is the POPOVER up — the mount and the band
  //
  // §14.5a is the lesson this obeys: a predicate that re-states four gate
  // conditions from memory got three of them wrong, and everything looked
  // right in the one case somebody tested. Add a sixth tenant and it joins
  // the right family here, in one place, and nothing else changes.

  /** The profile menu, which is NOT a column. `POPOVERS.md` §1. */
  protected readonly profilePopoverOpen = computed(
    () => this.shell.rightPanel() === 'profile',
  );

  /**
   * What the slot may mount. `'none'` for profile, so the `@switch` has no
   * arm for it and the `<aside>` cannot render an empty 460px plate.
   */
  protected readonly slotTenant = computed<RightPanel>(() => {
    const panel = this.shell.rightPanel();
    return panel === 'profile' ? 'none' : panel;
  });

  /** Is a COLUMN up in the slot? Derived from `slotTenant`, never re-stated. */
  protected readonly columnOpen = computed(() => this.slotTenant() !== 'none');

  /**
   * §14.2 — HOW FAR THE PAGE'S OWN NAVIGATION MUST STOP SHORT, and nothing
   * else. Published inline on `.app-shell` from render values (§14.7:
   * render demonstrably runs on every state change; a lifecycle hook does
   * not, and two publication paths that can disagree is worse than either).
   *
   * ⚠️ THIS IS NOT `--right-panel-inset` COMING BACK. That property was
   * what `.main-surface` padded by, and the padding is what moved the page.
   * It is deleted and stays deleted: `.main-surface` has no rule that reads
   * this token, and the page is laid out identically whether a column is up
   * or not — measured, closed vs open, in this lane's report.
   *
   * What it does drive is the two halves of the NOTCH, which are separable
   * from the inset and were only ever removed alongside it:
   *   - the width of the `::before` that repaints the strip of bar above the
   *     column in the panel surface, so the two read as one plane;
   *   - the ceiling on the bar's PAGE-LEVEL group, so the view tabs
   *     terminate at the column's edge instead of appearing printed on it.
   *
   * `0px` when nothing is up, and `0px` for a SHEET: below the narrow
   * breakpoint the column covers the whole bar, so there is no bar left to
   * notch and nothing that needs to dodge it (§14.5d — feeding a sheet's
   * full-viewport width to this collapsed the header group to zero).
   */
  protected readonly panelClear = computed(() =>
    this.columnOpen() && !this.compactNav() ? 'var(--panel-w)' : '0px',
  );

  /**
   * How far the issue panel stands off the right edge: clear of an open column plus a gap, so Ask stays on screen
   * beside it (IssuePanelService: "keeping Ask open"). Nothing at sheet width, where the panel takes over.
   */
  protected readonly issuePanelClear = computed(() =>
    this.panelClear() === '0px' ? '0px' : 'calc(var(--panel-w) + 12px)',
  );

  // ── The profile popover's band registration ─────────────────────────────

  /**
   * The avatar in the bar — the trigger focus returns to on close.
   *
   * `{ read: ElementRef }` IS LOAD-BEARING. A template reference on a
   * COMPONENT element resolves to the component INSTANCE by default, so
   * `viewChild<ElementRef>('profileAvatar')` type-checks, compiles, runs, and
   * hands back a `ProfileAvatarComponent` whose `.nativeElement` is
   * `undefined`. The trigger then arrives at the band as `undefined`,
   * `restoreFocus` early-returns on it, and Escape leaves focus on `<body>` —
   * silently, and identically to the `_triggers`-map no-op §14.5b records.
   * Measured before and after: `AVATAR.focus()` was never called.
   */
  private readonly profileAvatar = viewChild('profileAvatar', { read: ElementRef });

  private readonly band = inject(PopoverBandService);

  /**
   * Enrol the profile menu in the dismissal band (`POPOVERS.md` §3/§4).
   *
   * Registered from the SHELL rather than from the panel because the band
   * needs the trigger element, and the trigger lives in this template. The
   * panel supplies its own `data-popover` root — presence in the DOM is
   * openness, so the band needs no second signal to consult.
   *
   * `untracked` around the body is not decoration. `band.open()` runs mutual
   * exclusion, which invokes every other popover's close callback, and those
   * callbacks write signals; reading them inside this effect's tracked body
   * would make the effect a dependency of surfaces it merely closed. The
   * shape is the one `docs/POLISH_AUDIT.md § Signal cycles` exists to stop.
   */
  // ── Band popovers grow out of their triggers ────────────────────────
  //
  // The location picker and the profile menu are this app's own components,
  // so they open on the motion kit's `pop` (--spring, out of the trigger) and
  // recede back into it (--ease-sharp). The animation runs on the fixed
  // `[data-popover]` plate INSIDE each host (`within`), never on the host: a
  // transform there would become the plate's containing block — the bug the
  // popover-band comment in the template describes. The trigger is captured
  // on enter because the band forgets it the moment the popover closes.
  private locationPopAnchor: HTMLElement | null = null;
  private profilePopAnchor: HTMLElement | null = null;
  private readonly locationPopIn = enterMotion({ motion: 'pop', within: '[data-popover]', anchor: () => this.locationPopAnchor });
  private readonly locationRecede = leaveMotion({ within: '[data-popover]', anchor: () => this.locationPopAnchor, scale: 0.96 });
  private readonly profilePopIn = enterMotion({ motion: 'pop', within: '[data-popover]', anchor: () => this.profilePopAnchor });
  private readonly profileRecede = leaveMotion({ within: '[data-popover]', anchor: () => this.profilePopAnchor, scale: 0.96 });

  protected onLocationPopoverEnter(event: AnimationCallbackEvent): void {
    this.locationPopAnchor = this.chip.anchor();
    this.locationPopIn(event);
  }
  protected onLocationPopoverLeave(event: AnimationCallbackEvent): void {
    this.locationRecede(event);
  }
  protected onProfilePopoverEnter(event: AnimationCallbackEvent): void {
    this.profilePopAnchor = this.band.triggerFor('shell.profile');
    this.profilePopIn(event);
  }
  protected onProfilePopoverLeave(event: AnimationCallbackEvent): void {
    this.profileRecede(event);
  }

  private registerProfilePopover(): void {
    const KEY = 'shell.profile';
    effect(() => {
      const open = this.profilePopoverOpen();
      untracked(() => {
        if (!open) {
          this.band.close(KEY);
          return;
        }
        // The workspace nav hands over the control that was pressed (the
        // organisation mark or the avatar), so the menu grows out of it and
        // focus returns to it; opened any other way, the nav's avatar stands in.
        const navHost = this.workspaceNav?.location.nativeElement as HTMLElement | undefined;
        const navTrigger = this.workspaceNavProfileTrigger?.isConnected
          ? this.workspaceNavProfileTrigger
          : (navHost?.querySelector<HTMLElement>('.frost-avatar') ?? null);
        const host = (this.profileAvatar()?.nativeElement as HTMLElement | undefined) ?? null;
        // The host is a plain element; the focusable control is the button
        // inside the packaged avatar component. Focus must land on something
        // that can take it, or the return is a silent no-op onto `<body>`
        // (§14.5b, and `POPOVERS.md` §5).
        const trigger = navTrigger ?? host?.querySelector<HTMLElement>('button') ?? host;
        this.band.open(KEY, {
          close: () => this.shell.closeRightPanel(),
          trigger,
        });
      });
    });
    this.destroyRef.onDestroy(() => this.band.close(KEY));
  }

  private registerSlotAsColumn(): void {
    const SLOT_ID = 'shell-slot';
    effect(() => {
      // `columnOpen()`, not `rightPanelOpen()`. Registering the slot while
      // the profile POPOVER is up would register a column with nothing in
      // it — and the notch, which reads the registry, would paint a 460px
      // plate of panel surface across the bar with no panel beneath it.
      if (this.columnOpen()) {
        // No `yields` — the option is inert now that nothing insets.
        this.sidePanel.open(SLOT_ID, { surfaceScoped: false });
      } else {
        this.sidePanel.close(SLOT_ID);
      }
    });
  }

  /**
   * Escape closes the slot (§5.5). Document-level because the slot's four
   * tenants do not all handle it themselves, and because the user may be
   * anywhere on the page — the panel is non-modal, so focus is often still
   * out on the canvas.
   *
   * The POPOVER BAND answers first. `PopoverBandService` holds a
   * capture-phase Escape listener and stops propagation while a popover is
   * open, so this bubble-phase host binding never runs underneath one —
   * popovers layer above columns (z-90 vs z-40/97) and Escape always
   * dismisses the topmost thing.
   *
   * Yields to anything layered ABOVE it. The command palette, the shortcuts
   * sheet and every `aria-modal="true"` dialog own Escape while they are up;
   * closing the panel underneath them would be the wrong surface responding
   * to the key, and would leave the modal open with its backdrop intact.
   */
  protected onShellEscape(event: Event): void {
    // `columnOpen()`, not `rightPanelOpen()`. The profile popover is the
    // band's to dismiss, and the band already stops propagation in document
    // capture while one is up — but a predicate that still counted profile
    // as "the slot is open" would swallow Escape for a surface this handler
    // no longer closes, on any path where the band did not run first.
    if (!this.columnOpen()) return;
    if (this.cmdk.open() || this.shell.shortcutsDialogOpen()) return;
    if (this.hasVisibleModal()) return;
    event.stopPropagation();
    this.shell.closeRightPanel();
  }

  /**
   * Is a true modal currently ON SCREEN? Not merely present, and not merely
   * laid out.
   *
   * The shell mounts the people panel, the project panel, What's New and the
   * Book Meeting Panel unconditionally, each keeping its `role="dialog"`
   * `aria-modal="true"` markup and a full 540×900 rect while closed — they
   * hide with `transform: translateX(100%)`, which is a transition that can be
   * animated and `display: none` is not. So `display` and `visibility` are
   * both the wrong question; the right one is "does its box intersect the
   * viewport", which a translated-out panel answers no to.
   */
  private hasVisibleModal(): boolean {
    const candidates = document.querySelectorAll<HTMLElement>(
      '[role="dialog"][aria-modal="true"], [role="alertdialog"]',
    );
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    for (const el of candidates) {
      if (el.closest('.right-panel')) continue; // the panel is not above itself
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      if (rect.right <= 0 || rect.bottom <= 0) continue;
      if (rect.left >= vw || rect.top >= vh) continue;
      const style = getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      if (Number(style.opacity) === 0) continue;
      return true;
    }
    return false;
  }

  /**
   * Visual treatment for the floating glass bar based on the active route.
   * Drives `<paper-glass-bar [variant]>`:
   *   - 'home'     when on `/home` — busiest backdrop, strongest blur.
   *   - 'admin'    when in any admin scope (settings rails, dense pages).
   *   - 'schedule' when on `/schedule` — faint emerald tint.
   *   - 'plain'    everywhere else.
   */
  protected readonly currentGlassVariant = computed<PaperGlassBarVariant>(() => {
    if (this.shell.isHome()) return 'home';
    if (this.shell.isSchedule()) return 'schedule';
    if (this.inAdminScope() || this.shell.currentSection() === 'settings') return 'admin';
    return 'plain';
  });

  // ── Navigation organism (`paper-shell-bar`) ──────────────────────────────
  //
  // The shell OWNS no navigation logic. It projects the registry
  // (`core/constants/navigation.ts`) onto the organism's contract and routes
  // the outputs back through the same registry. Everything else — the shed
  // ladder, tab-scroll-into-view, roving tabindex, the ⌥ keycap reveal, the
  // overflow menu and its includeCore rule, avatar containment — belongs to
  // `paper-shell-bar` and is covered by its own 63 behavioural assertions.
  //
  // ANALYTICS: the organism reports its own interactions through
  // `PAPER_ANALYTICS`, which `providePaperBridges()` already forwards into
  // PostHog as `paper.component.interaction`. The shell therefore fires NO
  // event for a workspace tile, an overflow-menu pick or a view-track
  // selection — re-firing would reintroduce the composition double-count
  // Paper 0.11.0 just fixed. The one surface the organism does not report is
  // the tenant lockup; that gets an app event (SHELL_LOCKUP_SELECTED).

  // ── Context and back (§2 of the navigation contract) ─────────────────────
  //
  // A CONTEXT is any named entity you have drilled into. Supply one and the
  // bar collapses its breadth, hosts a back arrow in the crumb, swaps in the
  // context's views and builds a sibling switcher — all inside Paper. The
  // shell's whole job is to say WHICH entity, from the URL plus the stores
  // that already hold it.
  //
  // BACK, UP AND ACROSS ARE THREE CONTROLS (§2.2), and only one of them owns
  // movement of its own:
  //   - back   → `NavHistoryService.back()` → `Location.back()`. One history,
  //              annotated; see that service for why a second stack cannot
  //              stay in sync with the browser's own button.
  //   - up     → the crumb's root row. A fixed destination, so a plain
  //              navigation.
  //   - across → the sibling switcher. A plain navigation to a peer.

  private readonly navHistoryService = inject(NavHistoryService);
  private readonly communities = inject(CommunitiesService);
  private readonly publicProfile = inject(PublicProfileService);

  /**
   * The stack behind the current entry. `paper-shell-bar` labels its back
   * affordance from the LAST entry and renders no arrow at all when the
   * array is empty — which is the correct answer on a cold deep link, where
   * there is no path to retrace. Up (the crumb's root row) covers that case.
   */
  protected readonly navHistory = this.navHistoryService.entries;

  /** The community id the URL is inside, or null. */
  private readonly communityContextId = computed(() =>
    communityIdFromUrl(this.activeUrl() ?? ''),
  );

  /** The colleague id the URL is inside, or null. */
  private readonly personContextId = computed(() =>
    personIdFromUrl(this.activeUrl() ?? ''),
  );

  /**
   * The named entity the user has drilled into, or null at a workspace root.
   *
   * FAILS CLOSED, like every other capability read in this file: a community
   * context needs the `communities` key, so until the viewer's flag set
   * resolves the bar shows no crumb, no peers and no community views. The
   * sibling list is the reason that matters — it is a NEW source of community
   * names, and a new source must not leak what the registry's other gates
   * keep hidden.
   */
  /**
   * Any Studio URL, on either mount. `/content` and `/preview/content`.
   *
   * THE REGISTRY ANSWERS IT NOW, and the regex it replaced was wrong in a way
   * this lane would have made visible. `/(^|\/)(preview\/)?content(\/|$)/`
   * matches a `content` SEGMENT anywhere, so it was true on `/admin/content` —
   * a live admin page belonging to another surface, which has been wearing the
   * Studio's context crumb. Harmless while the Studio's `views` were `[]`
   * (an empty array stands the track down); the moment its five views arrived
   * the same predicate would have hung the Studio's tabs across the top of
   * somebody else's admin page. See `isStudioUrl`.
   */
  private readonly inContentStudio = computed(() => isStudioUrl(this.activeUrl()));

  protected readonly navContext = computed<PaperNavContext | null>(() => {
    this.locale.locale(); // track for reactivity
    const communityId = this.communityContextId();
    if (communityId) return this.communityContext(communityId);
    const personId = this.personContextId();
    if (personId) return this.personContext(personId);
    if (this.inContentStudio()) return this.studioContext();
    return null;
  });

  /**
   * The Content Studio is a place you drill INTO, so it gets a context.
   *
   * A context is described here as "any named entity you have drilled into",
   * and the Studio stretches that slightly: it is a named PLACE rather than a
   * row. It earns one for the same reason a community does — you arrive from
   * somewhere (Discover, a community, the nav), you move around inside it
   * across four sections, and you need a way out that is not the browser
   * button.
   *
   * It carried its own Back control until now, which was the wrong shape:
   * every other deep surface puts back in the crumb, so the Studio had a
   * second back affordance in a place nothing else does, and the two would
   * have diverged the first time the crumb's behaviour changed.
   *
   * `kind: 'article'` because that is what the Studio manages, and it is
   * already in Paper's kind union — no new kind, no new tint to define.
   *
   * NO SIBLINGS: that would mean "other studios", and there is one.
   *
   * THE VIEWS WERE `[]` WITH A NOTE SAYING "views would duplicate the rail".
   * True, and the conclusion was the wrong way round: they were one job done
   * twice, and the copy to keep is the one every other level two in this app
   * already uses. The Studio's rail has gone and its sections arrive here.
   *
   * ─── FILLED DIRECTLY, NOT THROUGH `toContextNavViews` ───────────────────
   *
   * A community's views go through that projection because they are
   * capability-gated and the bar must fail CLOSED on them — the sibling
   * switcher is a new source of community names and must not leak what the
   * other gates hide. The Studio's do not, and routing them through it would
   * be actively wrong: `/preview/content` is unauthenticated by design, so
   * `enabledFlags()` there holds nothing, and failing closed over an empty set
   * renders an EMPTY TRACK on the single surface that exists to be reviewed
   * without a login. The gate that matters is already upstream — the whole
   * mount is behind `contentCentreGuard()` and `featureFlagGuard('content.
   * creation')`, and the one entry with a mount of its own to respect
   * (curation) is resolved from the ROOT rather than from a flag.
   *
   * THE COUNT IS `null` AND THAT IS NOT A PLACEHOLDER. The honest badge here
   * is Community's open moderation queue, and `ContentModerationService` is
   * provided by the Studio's own route — deliberately, because its query is
   * gated on `content:moderate` and most viewers get a FORBIDDEN. The shell
   * sits above that injector and has nobody to ask. `studioNavViews` OMITS the
   * badge entirely for a null rather than passing the treatment through with
   * no value, because Paper degrades a countless count badge to a presence
   * dot — and a dot over a number we never fetched says "there is something
   * waiting" about a queue nobody has read.
   */
  private studioContext(): PaperNavContext {
    const label = this.t('content_studio.title', 'Content Studio');
    return {
      kind: 'article',
      id: 'content-studio',
      label,
      short: label,
      mark: null,
      tone: null,
      views: studioNavViews(
        // The root comes from the URL, never assumed — the Studio is mounted
        // twice and the preview harness does not carry the curation route.
        studioRootForUrl(this.activeUrl() ?? ''),
        (key, fallback) => this.t(key, fallback),
        null,
      ),
      siblings: [],
      rootLabel: '',
      groupLabel: '',
    };
  }

  /**
   * A community as a context. Label, mark, peers and views all come from the
   * SAME `CommunitiesService` singleton the widget and the page read, so the
   * bar cannot disagree with the surface underneath it.
   *
   * On a hard reload none of that is in memory yet, which would flash an
   * unnamed crumb — `communityContextResolver` fills the store before the
   * route activates so the first paint is already correct.
   */
  private communityContext(id: string): PaperNavContext | null {
    if (!this.enabledFlags().has('communities')) return null;
    const record = this.communities.find(id);
    const label =
      record?.name ?? this.t('shell.nav_context.community_fallback', 'Community');
    return {
      kind: 'community',
      id,
      label,
      // Paper ellipsises the crumb name and sheds it entirely below 1200px,
      // so the honest `short` is the real name — §7.1 forbids truncating it
      // and the record carries no short form to truncate to.
      short: label,
      mark: record ? getInitials(record.name) : null,
      tone: communityTone(record),
      views: toContextNavViews(COMMUNITY_VIEWS, this.enabledFlags(), {
        localise: view => this.t(view.labelKey, view.label),
        // Record-data gates (Apps needs a custom community, Links needs a
        // link or the right to add one) live in ONE predicate the detail
        // page shares, so the bar and the page cannot disagree about which
        // tabs a community has.
        applies: view => communityViewApplies(view, record),
        activeId: this.activeNavViewId(),
      }),
      siblings: this.communitySiblings(record),
      rootLabel: this.t('shell.nav_context.browse_communities', 'Browse all communities'),
      groupLabel: this.t('shell.nav_context.your_communities', 'Your communities'),
    };
  }

  /**
   * The peers the switcher offers — the viewer's own communities, with the
   * one they are looking at included even when they have not joined it.
   *
   * A switcher that omits where you are reads broken: Paper ticks the active
   * row from the context id, and with no matching row there is nothing to
   * tick. `mine()` is a viewability-enforced server list, and the whole
   * context is gated on `communities` above, so nothing here widens what the
   * viewer can see.
   */
  private communitySiblings(record: Community | null): PaperNavContextSibling[] {
    const mine = this.communities.mine();
    const all = record && !mine.some(c => c._id === record._id) ? [record, ...mine] : mine;
    return all.map(community => ({
      id: community._id,
      label: community.name,
      mark: getInitials(community.name),
      tone: communityTone(community),
      // Unread beats headcount — it is the reason to switch. Both are
      // OMITTED rather than rendered as zero: a record whose `memberCount`
      // did not come back rendered a bare "members" under the name, and a
      // row that shows one honest number is better than one that shows a
      // sentence with a hole in it. Paper guards on a truthy `meta`.
      ...metaLine(
        community.unreadCount
          ? this.transloco.translate('home_preview.communities.detail.hero.unread', {
              count: community.unreadCount,
            })
          : community.memberCount
            ? this.transloco.translate(
                'home_preview.communities.detail.member_count',
                { count: community.memberCount },
              )
            : '',
      ),
    }));
  }

  /**
   * A colleague as a context — the cheapest fix in the app for the worst
   * strand in it. `/people/:id` had NO back affordance of any kind, is
   * cold-linkable, and (resolving to no section of its own) rendered Home's
   * four tabs with none of them active.
   *
   * NO VIEWS AND NO SIBLINGS, both deliberate. `views: []` is not the same as
   * omitting them — Paper reads `context.views ?? workspace.views`, so an
   * empty array is what STANDS THE TRACK DOWN and stops the bar claiming you
   * are on Home. And a colleague has no peer set worth offering: there is no
   * people directory to be the root of, so the crumb renders as a readout
   * with a back arrow, and up is the workspace pill.
   */
  private personContext(id: string): PaperNavContext {
    const person = this.publicProfile.person();
    const named = person && person._id === id ? person.name : '';
    const label = named || this.t('shell.nav_context.person_fallback', 'Profile');
    return {
      kind: 'person',
      id,
      label,
      short: label,
      mark: named ? getInitials(named) : null,
      tone: null,
      views: [],
      siblings: [],
      rootLabel: '',
      groupLabel: '',
    };
  }

  // ── The context chip — location · floor · time (§2.5) ────────────────────
  //
  // Paper supplies the chip; the app supplies the model behind it and the menu
  // in front of it. Both bindings are MAP-ONLY for now, and deliberately:
  //
  //   - The time segment exists only where time can be SCRUBBED, and the map
  //     is the only such surface. Rendering it elsewhere would be a dead
  //     control, which §2.5 calls out by name.
  //   - A building switcher on Home and Discover is a product change beyond
  //     this track — those surfaces read the location, they do not offer to
  //     change it. Widening later is one predicate here, nothing else.

  protected readonly chip = inject(MapContextChipService);

  /**
   * Whether this viewport has room for the chip at all.
   *
   * §2.6's shed ladder runs 1440 → 820 and the utility cluster is `flex: none`,
   * so below the narrow breakpoint the chip stops shedding and starts pushing:
   * measured at 390px it squeezed the lead cluster — lockup, workspace pill —
   * to 22px. The bar is simply over-subscribed there.
   *
   * Nothing becomes unreachable. Below this width the bottom nav owns
   * workspace switching, `im-right-stack`'s floor indicator owns the floor,
   * and `map-commands.service.ts` registers a command per building AND per
   * floor, so ⌘K reaches both. A clipped control would be the worse answer.
   */
  private readonly chipFits = computed(() => !this.compactNav());

  /** The chip's left segment, or null on every surface that owns no site. */
  protected readonly chipLocation = computed<PaperNavLocation | null>(() =>
    this.isMapSurface() && this.chipFits() ? this.chip.location() : null,
  );

  /** The chip's right segment. Null is "render nothing", not "render empty". */
  protected readonly chipTime = computed<PaperNavTime | null>(() =>
    this.isMapSurface() && this.chipFits() ? this.chip.time() : null,
  );

  /**
   * The element Paper handed us to anchor the menu to.
   *
   * Read by `im-location-picker` through `MapContextChipService.anchor` —
   * `anchorFrom` measures its rect and produces the fixed box. The shell no
   * longer positions the menu at all.
   *
   * ⚠️ `chipMenuPositions` LIVED HERE, and deleting it is the fix, not a
   * tidy-up. It was a `ConnectedPosition[]` for a `cdkConnectedOverlay`
   * carrying `offsetY: 8`, and CDK applies an offset by writing
   * `transform: translateY(8px)` INLINE on the overlay pane. A transformed
   * ancestor is the containing block for `position: fixed`, so the picker's
   * measured viewport coordinates were re-based onto the pane's origin and
   * the panel painted 632px off the right of a 1512px window — open,
   * visible, unreachable. The stylesheet's `transform: none` on the pane
   * could never win against an inline declaration. The whole overlay is
   * gone; see the popover-band block in `app-shell.component.html`.
   */
  protected readonly chipAnchor = this.chip.anchor;

  /** A chip segment asked for its menu. Same trigger twice closes it. */
  protected onChipSegment(segment: ContextChipSegment, trigger: HTMLElement): void {
    if (this.chip.openSegment() === segment) {
      this.chip.close();
      return;
    }
    this.analytics.capture(ANALYTICS_EVENTS.CONTEXT_CHIP_OPENED, {
      segment,
      section: this.shell.currentSection() ?? 'home',
      site_count: this.chip.sites().length,
      floor_count: this.chip.activeSite()?.floors?.length ?? 0,
      live: this.chip.live(),
    });
    this.chip.open(segment, trigger);
  }

  /** Esc closes and returns focus to the trigger (§2.7). */
  protected onChipMenuKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    this.chip.close();
  }

  /** Localise a registry entry, falling back to its English string. */
  private t(key: string, fallback: string): string {
    return this.transloco.translate(key) || fallback;
  }

  /**
   * The human name of one back-stack frame — what "Back to …" reads.
   *
   * An ENTITY beats its container: returning to a community should say the
   * community's name, not "Home". Falls through to the workspace's view and
   * then to the workspace itself, which is what a frame outside any context
   * gets.
   */
  private frameLabel(frame: NavHistoryFrame): string | null {
    this.locale.locale(); // track for reactivity
    if (frame.contextId) {
      const community = this.communities.find(frame.contextId);
      if (community) return community.name;
      const person = this.publicProfile.person();
      if (person && person._id === frame.contextId) return person.name;
    }
    const workspace = findWorkspace(frame.workspaceId);
    // Three sections are real surfaces with no workspace to borrow a label
    // from — `profile`, `ai-chat` and `apps` have no tile, no shortcut and
    // no pill. Without this the crumb read "Back to profile": the raw
    // section id, lowercase and untranslated.
    if (!workspace) {
      return this.t(`shell.sections.${frame.workspaceId}`, frame.workspaceId);
    }
    const view = frame.viewId
      ? viewsForUrl(workspace, frame.url).find(candidate => candidate.id === frame.viewId)
      : undefined;
    return view
      ? this.t(view.labelKey, view.label)
      : this.t(workspace.labelKey, workspace.label);
  }

  /**
   * THE FIFTH INPUT — every workspace, already filtered to the viewer's
   * capabilities AND grants. The bar renders its tiles and its overflow menu
   * straight off this list, so the filter here IS the gate: the same
   * `isWorkspaceVisible` ANY-OF the bottom-nav strip and the route guards
   * use, plus the ANY-OF over `actionKeys` for the workspaces that declare
   * them. Both fail closed while the viewer is still loading.
   *
   * `canDo` reads `ViewerIdentityService.actions()` through
   * `ProfileDataService`, which is a signal, so this computed re-runs when the
   * viewer's grants arrive — the same reactivity `enabledFlags()` gives the
   * capability half.
   *
   * The fifth argument is the workspace the URL is in, and it is the SAME id
   * the bar's `workspace` input carries — so a destination reached through the
   * overflow (Live events, Visitors, Insights, Content Studio) rails itself
   * while you are on it and the tile the organism marks `aria-current="page"`
   * is one that exists. The rule lives in `withActiveRailed`, next to the
   * `activeId` hatch `toNavViews` uses one level down; the shell only says
   * where it is.
   */
  protected readonly navWorkspaces = computed<readonly PaperNavWorkspace[]>(() => {
    this.locale.locale(); // track for reactivity
    return toNavWorkspaces(
      this.enabledFlags(),
      workspace => this.t(workspace.labelKey, workspace.label),
      workspace =>
        workspace.descriptionKey
          ? this.t(workspace.descriptionKey, workspace.description ?? '')
          : workspace.description,
      action => this.profileData.canDo(action),
      this.activeWorkspaceItem().id,
      flag => this.posthogFlags.flagNotOff(flag),
    );
  });

  /**
   * The registry entry for the workspace the URL is inside. `/profile` has no
   * workspace of its own — it is where the Settings destination resolves for
   * non-admins — so it borrows Settings'. Anything else unknown falls back to
   * Home, which always exists.
   */
  private readonly activeWorkspaceItem = computed<WorkspaceItem>(() => {
    const section = this.shell.currentSection() ?? 'home';
    return (
      findWorkspace(section) ??
      findWorkspace(section === 'profile' ? 'settings' : 'home') ??
      NAV_FALLBACK_WORKSPACE
    );
  });

  protected readonly navWorkspace = computed<PaperNavWorkspace>(() => {
    this.locale.locale(); // track for reactivity
    const workspace = this.activeWorkspaceItem();
    return toNavWorkspace(
      workspace,
      this.t(workspace.labelKey, workspace.label),
      workspace.descriptionKey
        ? this.t(workspace.descriptionKey, workspace.description ?? '')
        : workspace.description,
    );
  });

  /**
   * Level two for the active workspace — capability-filtered ANY-OF through
   * the registry, exactly as the hand-rolled Home and Schedule tab strips
   * were. A gated view vanishes here; the route guards and
   * `ScheduleComponent`'s `?type=` / `?tab=` sanitiser catch the deep link.
   *
   * `activeId` carries over the strip's one escape hatch verbatim (it was
   * `homeGlassTabs().discover || homeSurfaceActive('discover')`): the view
   * you are already looking at stays listed even when gated, because the
   * guard that let you in is the real gate and the track would otherwise
   * render a selection that is not in it.
   */
  protected readonly navViews = computed<readonly PaperNavView[]>(() => {
    this.locale.locale(); // track for reactivity
    const views = toNavViews(this.activeWorkspaceItem(), this.enabledFlags(), {
      localise: view => this.t(view.labelKey, view.label),
      activeId: this.activeNavViewId(),
      // The Approvals view's pending count (`approvals.count`): an approver's badge.
      count: view => (view.id === 'approvals' ? this.approvals.count() : undefined),
      // Schedule serves two routes with different level-twos — the redesign's
      // five surfaces at `/schedule-preview`, the previous page's resource
      // types at `/schedule`. The registry picks the set; see `viewsForUrl`.
      url: this.activeUrl() ?? '',
    });
    return this.withApprovalsView(views);
  });

  /**
   * Home's Approvals entry. The registry holds it HIDDEN because a view's gate is a catalogue capability and this
   * one is an ACTION (`booking:approve`): an approver gets the entry, with the count of what waits, and nobody
   * else is offered it. Zero waiting shows no badge. Anyone on `/approvals` keeps the tab through `activeId`.
   */
  private withApprovalsView(views: ReturnType<typeof toNavViews>): readonly PaperNavView[] {
    if (this.activeWorkspaceItem().id !== 'home') return views;
    const count = this.approvals.count();
    const quiet = (view: PaperNavView): PaperNavView => (count > 0 ? view : { ...view, badge: 'none', count: undefined });
    const present = views.find(view => view.id === 'approvals');
    if (present) return views.map(view => (view.id === 'approvals' ? quiet(view) : view));
    if (!this.approvals.canApprove()) return views;
    return [
      ...views,
      quiet({
        id: 'approvals',
        label: this.t('shell.app_shell.surface_approvals', 'Approvals'),
        icon: 'check-circle',
        badge: 'count',
        count,
      }),
    ];
  }

  /**
   * Which view the URL is in — resolved entirely from registry data. Both
   * schedule routes express their level two as query-string STATES of one
   * path, so `isViewActive` matches the view's declared `queryParams` as well
   * as its prefix; workspaces whose views are distinct paths declare none and
   * match on the prefix alone. The shell used to carry a `?type=` / `?tab=`
   * regex for Schedule; the registry answers it now.
   */
  protected readonly activeNavViewId = computed<string | null>(() => {
    const url = this.activeUrl() ?? '';
    // Inside a community the track carries the CONTEXT's views, so the
    // selection is the `?tab=` state, resolved by the same registry helper
    // the page uses to pick its body. One reader, one answer.
    if (this.communityContextId()) return communityViewIdFromUrl(url);
    // Same shape for the Studio: the track carries ITS views, so the selection
    // is a registry answer rather than a third URL rule in this file.
    // `studioNavViewId` is `studioNavTarget` read backwards — one function
    // knows where a view lives, in both directions, so the track cannot send
    // you somewhere it then fails to highlight.
    if (this.inContentStudio()) return studioNavViewId(url);
    return (
      viewsForUrl(this.activeWorkspaceItem(), url).find(view => isViewActive(view, url))
        ?.id ?? null
    );
  });

  /**
   * Below the narrow breakpoint the bottom nav already owns workspace
   * switching, so the bar collapses its tiles into a single workspace pill
   * and spends the width on level two instead. `reveal` is the organism's
   * own input for exactly this; the shed ladder handles the rest.
   */
  private readonly compactNav = signal(false);

  protected readonly navReveal = computed<PaperShellBarReveal>(() =>
    this.compactNav() ? 'pill' : 'hybrid',
  );

  /** Division logo for the lockup's mark slot — the brand cap that used to
   *  be `im-org-switcher`'s own logo link. Suppressed inside the admin scope,
   *  where `im-admin-brand-block` is the sole brand cap (B11.2). */
  private readonly divisionBranding = inject(DivisionBrandingService);

  /**
   * The lockup's logo asset. Chosen against `chromePaper()` — the paper the
   * BAR prints on — not the document's. Under an article surface the bar can
   * be Midnight while `<html>` is still cream, and the light-paper (dark-ink)
   * variant would go invisible on the dark capsule. Tokens cascade into a
   * re-papered subtree; an asset picked in TypeScript does not, so it has to
   * be asked for explicitly.
   */
  protected readonly tenantLogo = computed<string | null>(() =>
    this.isInAdmin() ? null : this.divisionBranding.logoOn(this.chromePaper()),
  );

  /** Wordmark. Always the real division name — it is also what the lockup's
   *  accessible name is built from ("{tenant} — go to the start"), so it must
   *  never be blanked. The VISIBLE wordmark is hidden in SCSS when a logo is
   *  showing, because the logo already carries one. */
  protected readonly tenantName = computed<string>(
    () => this.divisionBranding.branding().name,
  );

  protected readonly tenantMark = computed<string>(() =>
    getInitials(this.divisionBranding.branding().name),
  );


  // Bottom navigation — primary workspace nav. Rendered through
  // <paper-bottom-nav /> (Theme System Revamp P3.3) which owns the floating
  // glass pill chrome, scroll-fade, and the inner pill-switcher.
  //
  // Membership, order, icons and labels all come from the navigation registry
  // (`core/constants/navigation.ts`): a workspace joins the strip by declaring
  // a `bottomNavOrder`. Visitors and Insights deliberately have none — they
  // stay reachable from the command palette and deep links without taking a
  // pill. The Flow pill's accent treatment (emerald hover + active tint) lives
  // in paper-bottom-nav.component.scss, targeting `paper-nav-tab[data-key="flow"]`.
  protected readonly bottomNavPills = computed<readonly BottomNavItem[]>(() => {
    // Track locale so labels update when the user changes language.
    this.locale.locale();
    // Capability gate (ANY-OF over each workspace's `capabilityKeys`) —
    // gated entries (map, schedule, flow, games, world) drop out of the pill
    // strip when the customer's capability is off. Home and settings
    // carry no keys so they always render. Fails closed until the
    // viewer flag set loads.
    const on = this.enabledFlags();
    return BOTTOM_NAV_WORKSPACES
      .filter(workspace => isWorkspaceVisible(workspace, on, flag => this.posthogFlags.flagNotOff(flag)))
      .map(workspace => ({
        key: workspace.id,
        label: this.transloco.translate(workspace.labelKey) || workspace.label,
        icon: workspace.icon,
      }));
  });
  protected readonly activeNavKey = computed(() => this.shell.currentSection() ?? 'home');
  protected readonly isMapSurface = computed(
    () => this.shell.currentSection() === 'map',
  );
  /**
   * How this section wants tenant building alerts presented — registry data,
   * not a route test. See `SectionChrome.alertPresentation`.
   */
  protected readonly alertPresentation = computed(
    () => sectionAlertPresentation(this.shell.currentSection() ?? 'home'),
  );
  protected readonly mapAlertZIndex = Z_INDEX.POPUP;

  /** Unread critical/urgent items for the persistent banner — visible on ALL pages */
  protected readonly urgentBannerItems = computed(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return this.newsApi.newsItems()
      .filter(n =>
        (n.urgency === 'critical' || n.urgency === 'urgent') &&
        !n.isRead &&
        (!n.createdAt || new Date(n.createdAt) >= sevenDaysAgo),
      )
      .sort((a, b) => {
        // Critical before urgent
        const tierA = a.urgency === 'critical' ? 0 : 1;
        const tierB = b.urgency === 'critical' ? 0 : 1;
        if (tierA !== tierB) return tierA - tierB;
        // Newest first within same tier
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeB - timeA;
      })
      .slice(0, 3);
  });

  /** Only urgent building alerts interrupt the workspace. Routine notices
   *  live in Notifications; booking closures still enforce their restrictions
   *  in the relevant booking surfaces without implying urgent severity. */
  private readonly _buildingAlerts = inject(BuildingAlertsService);
  private readonly locationState = inject(LocationStateService);
  private readonly viewedShellAlerts = new Set<string>();
  protected readonly tenantUrgentAlerts = computed<BuildingAlert[]>(() => {
    const dismissed = this._buildingAlerts.dismissedIds();
    const locationId = this.isMapSurface()
      ? this.locationState.selectedLocationId()
      : null;
    return this._buildingAlerts
      .activeAlerts()
      .filter(
        (alert) =>
          alert.severity === 'urgent' &&
          !dismissed.has(alert._id) &&
          (!locationId ||
            alert.targets.some((target) => target.locationId === locationId)),
      );
  });

  /** The full-measure band beneath the bar — every section but the map,
   *  which floats the beacon instead (`alertPresentation`). */
  protected readonly showTenantAlertStrip = computed(
    () =>
      this.alertPresentation() === 'strip'
      && this.tenantUrgentAlerts().length > 0,
  );

  /** The compact counts pill floating over a full-bleed canvas. */
  protected readonly showAlertBeacon = computed(
    () =>
      this.alertPresentation() === 'beacon'
      && this.tenantUrgentAlerts().length > 0,
  );

  /**
   * Which shell surface these alerts are being read on. Derived from the
   * SAME registry axis that decides what renders, so the analytics can never
   * claim a band while a beacon is on screen.
   */
  private readonly alertSurface = computed<'shell_critical' | 'map_beacon'>(
    () => (this.alertPresentation() === 'beacon' ? 'map_beacon' : 'shell_critical'),
  );

  /** The band's expand toggle. It has exactly one way to collapse — the same
   *  header, clicked again — so it reports no `close_reason`. */
  protected onTenantAlertExpanded(expanded: boolean): void {
    this.captureAlertToggle(expanded);
  }

  /** The beacon's popover, which can also be closed by clicking away or by
   *  Escape. `reason` distinguishes reading the detail from brushing it off. */
  protected onBeaconExpanded(event: AlertBeaconToggle): void {
    this.captureAlertToggle(event.expanded, event.reason);
  }

  /**
   * "Read" on the urgent banner — THE READER, at `/article/:id`.
   *
   * The banner lives in the SHELL, so this door is on every route in the app,
   * and it was the last one still portalling `im-article-detail-panel` over
   * whatever page the reader happened to be on: an overlay with no URL, which
   * a reload loses and which Back closes the page underneath rather than the
   * thing on top of. Census: `features/discover/content-doors.ts`.
   *
   * An item with no id is not opened. `['/article', undefined]` resolves to
   * `/article`, which is not a route, so the honest answer is a warned no-op
   * over a 404 the reader cannot act on.
   */
  protected openUrgentArticle(item: NewsItem): void {
    const id = item._id?.trim();
    if (!id) {
      console.warn('[AppShell] urgent banner item has no id', { title: item.title });
      return;
    }
    void this.router.navigate(['/article', id]);
  }

  private captureAlertToggle(
    expanded: boolean,
    closeReason?: AlertBeaconToggle['reason'],
  ): void {
    const alerts = this.tenantUrgentAlerts();
    const surface = this.alertSurface();
    this.analytics.capture(
      ANALYTICS_EVENTS.BUILDING_ALERT_OVERLAY_COLLAPSE_TOGGLED,
      {
        surface,
        collapsed: !expanded,
        alert_count: alerts.length,
        blocking_alert_count: alerts.filter((alert) => alert.blockBookings)
          .length,
        ...(expanded || !closeReason ? {} : { close_reason: closeReason }),
      },
    );
    if (!expanded) return;
    for (const alert of alerts) {
      if (this.viewedShellAlerts.has(alert._id)) continue;
      this.viewedShellAlerts.add(alert._id);
      this.analytics.capture(ANALYTICS_EVENTS.BUILDING_ALERT_VIEWED, {
        alert_id: alert._id,
        severity: alert.severity,
        surface,
        block_bookings: !!alert.blockBookings,
      });
    }
  }

  protected async dismissTenantAlert(alert: BuildingAlert): Promise<void> {
    const surface = this.alertSurface();
    const dismissed = await this._buildingAlerts.dismiss(alert._id);
    if (!dismissed) return;
    this.analytics.capture(ANALYTICS_EVENTS.BUILDING_ALERT_DISMISSED, {
      alert_id: alert._id,
      surface,
      severity: alert.severity,
      block_bookings: !!alert.blockBookings,
    });
  }

  protected onTenantAlertCta(event: {
    alert: BuildingAlert;
    mode: 'internal' | 'external' | 'emit';
  }): void {
    this.analytics.capture(ANALYTICS_EVENTS.BUILDING_ALERT_CTA_CLICKED, {
      alert_id: event.alert._id,
      surface: this.alertSurface(),
      severity: event.alert.severity,
      block_bookings: !!event.alert.blockBookings,
      action_kind: event.mode,
    });
  }

  private readonly userPrefs = inject(UserPreferencesService);
  private readonly profileSettingsSync = inject(ProfileSettingsSyncService);
  protected readonly onboardingComplete = this.userPrefs.onboardingComplete;

  /**
   * A `/preview/*` harness, where the first-run tour must NOT render.
   *
   * ⚠ THESE ROUTES EXIST TO BE OPENED COLD, BY SOMEBODY WHO IS NOT SIGNED IN.
   * That is their whole purpose: review and Playwright, with no auth guard and
   * no real backend. Onboarding has never been complete for such a visitor, so
   * the tour opened over every harness and covered the surface under review —
   * and because it is a full-screen overlay it also swallows the clicks a test
   * makes at coordinates it can no longer reach.
   *
   * The e2e specs already worked around this by seeding
   * `im-user-preferences` with `onboardingComplete: true` before navigating,
   * with a comment naming the trap. A workaround every spec has to remember,
   * for a surface that should never have shown the tour, is the wrong shape:
   * fix it once, here, and the harnesses match what a reviewer expects.
   *
   * Matched on `/preview/` with both slashes ON PURPOSE — `/schedule-preview`,
   * `/home-preview` and `/schedule-v2-preview` are REAL routes a real person
   * signs into, and suppressing their onboarding would hide the tour from the
   * people it is for.
   */
  protected readonly isPreviewHarness = computed(() =>
    // …or a `?testHarness=1` review of a REAL guarded route, which is the
    // only way to open `/ai` cold. Constant for the page load by design; it
    // sits in a computed so the two review contexts share one gate.
    isPreviewHarnessUrl(this.activeUrl()) || isTestHarnessSession(),
  );

  protected readonly transitioning = signal(false);
  private transitionTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    const navFlagWait = setTimeout(() => this.navFlagWaitOver.set(true), AppShellComponent.NAV_FLAG_WAIT_MS);
    this.destroyRef.onDestroy(() => clearTimeout(navFlagWait));
    this.mountWorkspaceNavWhenShown();
    this.watchNavScroll();
    // An approver's nav count: read when they arrive, every two minutes, and when the tab is shown again.
    effect(() => {
      if (this.approvals.canApprove()) untracked(() => this.approvals.watch());
    });
    // Publish the three AI surfaces Command K's own Ask row does not cover, so
    // removing the top-bar Ask trigger does not strand them. Registered HERE
    // rather than from a page because the admin helper has no other affordance
    // at all — a contributor registered from Home would leave it unreachable
    // for anyone who never opened Home.
    this.aiCommands.register();
    // Flow's ⌘K commands (Start a session, Plan a session, New board, Open board…, Import a recording) and
    // the viewer's boards, on every surface: each follows its own flag.
    this.flowCommands.register();

    // Keep --top-banner-stack-height in sync with however many top banners
    // are visible, so stacked banners push the shell + glass bar down
    // instead of overlapping the content beneath them.
    this.setupBannerStackMeasurement();
    this.setupWorkspaceAlertMeasurement();

    // §5.5 — focus in on open, back to the trigger on close, for all four
    // tenants of the right-panel slot.
    this.manageSlotFocus();

    // §14 — and the slot pays the canvas through the column registry, so the
    // inset, the notch and the page-level clearance have ONE source.
    this.registerSlotAsColumn();

    // §6 — the profile menu is a band popover, and the band needs to be told.
    this.registerProfilePopover();
    // `?ask=<prompt>` — prefill (never send) the Ask composer, once, at startup.
    inject(AskDeepLinkService).start();

    // Pointing at, focusing or touching a link to the map starts the map's code, and the boot if it has not started yet.
    inject(MapIntentPrefetchService).start();

    // Defer map boot — don't block home page FCP with map GraphQL queries.
    // Boot after the page has settled so map data is pre-cached before navigation.
    const viewerBoot = inject(ViewerBootService);
    const deferBoot = (window as Window & { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback
      ?? ((cb: () => void) => setTimeout(cb, 2000));
    // Start loading the map engine chunk (mapbox-gl, Three.js, the map's services) with the boot, which needs it once the
    // floor plan arrives. Straight away when the page IS the map: nothing else can be shown until it is in.
    const settleEngine = (): void => { this.engineSettled.set(true); this.engineGate.prefetch(); };
    if (/^#\/map(?:[/?]|$)/.test(window.location.hash)) settleEngine();
    else afterFirstPaintSettled(settleEngine);
    // The first press anywhere (the location chip, the sidebar toggle, a room) means the engine-backed UI is about to be
    // wanted: start the chunk on the press, a click's length ahead of the defer that would otherwise start it.
    document.addEventListener('pointerdown', settleEngine, { once: true, passive: true, capture: true });
    // Synthetic previews have no employee session; the backend gate intentionally refuses map boot.
    // Read at callback time so navigation before idle keeps the destination's normal behaviour.
    deferBoot(() => { if (!sessionlessPreviewNow()) void viewerBoot.boot(); });
    // The Ask panels and ⌘K's Ask pane are @defer'd in their templates. Warm
    // their chunks once the page has settled, so a first open does not wait.
    deferBoot(() => {
      void import('./components/ai-panel/ai-panel.component').catch(() => undefined);
      void import('./components/cmdk/preview/cmdk-ask-pane.component').catch(() => undefined);
    });

    // IM-KEEP: hydrate building-alerts cache early so the urgent
    // banner doesn't flash empty on first render and so
    // PlatformDataService.closedSpaceIds is warm for the map.
    void this._buildingAlerts.loadActiveAlerts();
    void this._buildingAlerts.loadDismissedIds();

    // Ensure map commands are registered (book on behalf, etc.)
    inject(MapCommandsService);

    // Register catering commands (vendor search + "Book a meeting" action)
    inject(CommandCateringService);

    // Division branding is activated by injecting the service (`divisionBranding`
    // above) — its effect applies the brand colour to the CSS custom properties.

    // Collapse the bar's workspace tiles into a single pill on narrow
    // viewports, where the bottom nav already owns workspace switching.
    this.trackCompactNav();

    // Name the frames in the back-stack. `NavHistoryService` lives in core
    // and derives what the registry can — a workspace's own label — but only
    // the shell can name an ENTITY, so it supplies the resolver rather than
    // core importing the community and profile stores. Resolved at READ
    // time, so a community whose record arrives a moment after the
    // navigation still labels its own frame correctly, and so the label
    // re-localises when the user changes language.
    this.navHistoryService.setLabelResolver(frame => this.frameLabel(frame));

    // Announce route changes to screen readers in the active locale
    // (WCAG 4.1.3 Status Messages) — SPA navigations are otherwise silent.
    inject(RouteAnnouncerService).start();

    // Detect content blockers (ad blockers) that may interfere with features
    this.shell.detectAdBlocker();

    // If user navigates to /welcome, reset onboarding so they can re-view the tour
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      filter(e => e.urlAfterRedirects.includes('/welcome')),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      try { localStorage.setItem('ob-step', '0'); } catch { /* */ }
      this.profileSettingsSync.markOnboardingIncomplete();
    });

    // (Bottom-nav scroll-fade now lives inside <paper-bottom-nav /> — Theme System
    // Revamp P3.3 — so the shell no longer needs its own scroll listener for that.)

    this.router.events.pipe(
      filter(e => e instanceof NavigationStart),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.transitioning.set(true));

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (this.transitionTimer) clearTimeout(this.transitionTimer);
      this.transitionTimer = setTimeout(() => this.transitioning.set(false), 400);
    });

    // A launch from the nav belongs to the navigation it started (`navLaunch`).
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => (this.navLaunch = null));

    // (Keeping the active tab scrolled into view was a shell concern while
    // the tab strips were hand-rolled. `paper-view-track` owns it now —
    // centred by rect on every selection, view-set change and resize — so
    // the shell's NavigationEnd scrollIntoView has gone with the strips.)

    // Desktop-only shell wiring:
    //  - Cmd+Shift+Space global shortcut emits `menu:command-palette`
    //  - App menu emits `menu:settings` for Cmd+,
    // Subscribe once at construction so the bindings survive route changes.
    const unlistenPalette: Promise<() => void> = onShellEvent('menu:command-palette', () => {
      // The native Cmd+Shift+Space shortcut and the app menu land here. This
      // path deliberately bypasses `routeCommandKey`'s admin fork — the
      // desktop menu item says "Command palette", so it opens the employee
      // palette wherever you are.
      this.cmdk.openPalette('menu');
    });
    const unlistenSettings: Promise<() => void> = onShellEvent('menu:settings', () => {
      this.router.navigateByUrl(this.settingsDestination());
    });
    // Go menu (Home / Map / Schedule / Profile) — payload is the route slug.
    // Resolve workspace slugs through the navigation registry so Home +
    // Schedule land on the current default surfaces rather than on whichever
    // generation of the page a literal here happened to be written against.
    const unlistenNavigate: Promise<() => void> = onShellEvent<string>('menu:navigate', (slug: string) => {
      if (!slug) return;
      this.router.navigateByUrl(workspaceRoute(slug));
    });
    // Help → Check for updates… (desktop). Interactive check surfaces a
    // toast either way; UpdaterService is a no-op outside Tauri.
    const unlistenCheckUpdates: Promise<() => void> = onShellEvent('menu:check-updates', () => {
      void this.updater.check('menu');
    });
    // Desktop residency: the menu-bar line + quick actions, and the two
    // client-side reminders. Started here, not at boot, because both read
    // today's bookings and therefore need the session the shell implies.
    this.desktopTray.start();
    this.desktopReminders.start();
    // Flow's menu-bar item, ⌥⌘R and the floating recording widget (desktop main window only).
    this.desktopFlow.start();

    // Shell-owned global hotkeys. The shell mounts every destination, so it is
    // also the one place that registers the chords and resolves their
    // route-aware admin / Flow behavior.
    const unregisterPalette = this.hotkeys.register('mod+k', () => this.routeCommandKey(), {
      global: true, descriptionKey: 'shell.shortcuts_dialog.open_command_palette',
    });
    const unregisterPaletteP = this.hotkeys.register('mod+p', () => this.routeCommandKey(), {
      global: true, descriptionKey: 'shell.shortcuts_dialog.open_command_palette',
    });
    // Deliberately not global: an editable field keeps native ownership and,
    // on `/ai`, prevents a chord typed in the composer from clearing the
    // active conversation. Command K claims ⌘J in its own keymap while open.
    const unregisterAi = this.hotkeys.register('mod+j', () => this.routeAiKey(), {
      descriptionKey: 'shell.shortcuts_dialog.open_ai',
    });

    this.destroyRef.onDestroy(() => {
      if (this.transitionTimer) clearTimeout(this.transitionTimer);
      void unlistenPalette.then(off => off());
      void unlistenSettings.then(off => off());
      void unlistenNavigate.then(off => off());
      void unlistenCheckUpdates.then(off => off());
      unregisterPalette();
      unregisterPaletteP();
      unregisterAi();
    });
  }

  /** Hotkey path for ⌘K / ⌘P — same routing as the click handler. */
  private routeCommandKey(): void {
    if (this.isInAdmin()) {
      if (this.adminSearch.isOpen()) {
        this.adminSearch.close();
      } else {
        this.adminSearch.open(this.searchPillAnchor()?.nativeElement);
      }
      return;
    }
    // Deliberately AFTER the admin fork: Command K does not enter admin scope
    // in v1, and the admin section picker stays the ⌘K there.
    this.cmdk.toggle('shortcut', this.commandPaletteFocusTarget());
  }

  /** Hotkey path for ⌘J — admin keeps its helper, Flow keeps Flow
   *  Intelligence, and every ordinary employee surface opens the full-page
   *  Workplace AI agent. */
  private routeAiKey(): void {
    this.aiTrigger.route('keyboard');
  }

  /** Routes the global ⌘K / search-pill click. When the user is inside
   *  the admin route tree, opens the admin section picker anchored to
   *  the pill itself. Outside admin, opens Command K. Optional `trigger`
   *  is the wrapper element captured by template ref — falls back to the
   *  shell's own ViewChild lookup so the same routing applies to
   *  hotkey-triggered opens. */
  protected openCommandPalette(trigger?: HTMLElement): void {
    if (this.isInAdmin()) {
      const anchor = trigger ?? this.searchPillAnchor()?.nativeElement;
      this.adminSearch.open(anchor);
      return;
    }
    // Pass the actual control, not its layout wrapper, so focus returns to a
    // meaningful interactive element when the palette closes.
    this.cmdk.openPalette('click', this.commandPaletteFocusTarget(trigger));
  }

  /** Resolve the Paper search pill's native control from its projected
   * wrapper. Paper renders a normal button (not shadow DOM), so this stays a
   * DOM concern rather than adding focus API to the design-system component. */
  private commandPaletteFocusTarget(anchor?: HTMLElement): HTMLElement | undefined {
    const wrapper = anchor ?? this.searchPillAnchor()?.nativeElement;
    if (!wrapper) return undefined;
    if (wrapper.matches('button, [href], [tabindex]')) return wrapper;
    return wrapper.querySelector<HTMLElement>('paper-search-pill button, button') ?? undefined;
  }

  // ── Organism outputs → the registry ──────────────────────────────────────
  //
  // No analytics in any of these: `paper-shell-bar`, `paper-workspace-menu`
  // and `paper-view-track` each report their own surface through
  // PAPER_ANALYTICS, which the paper bridge forwards. The lockup is the one
  // exception — nothing in Paper reports it.

  /** A workspace picked on a tile, in the overflow menu, or with the `G`
   *  chord. Settings still resolves to the admin shell for admins and to
   *  personal settings for everyone else — see `settingsDestination`. */
  protected onWorkspaceSelected(workspace: PaperNavWorkspace): void {
    if (workspace.id === 'settings') {
      this.router.navigateByUrl(this.settingsDestination());
      return;
    }
    const item = findWorkspace(workspace.id);
    if (!item) return;
    void this.router.navigate([item.route]);
  }

  /** A view picked in the track — click, arrow keys or a `1`–`9` shortcut.
   *  The destination comes from the registry: a plain route for Home's
   *  surfaces, a route plus `?surface=` (the redesign) or `?type=` / `?tab=`
   *  (the previous page) state for Schedule's. Resolved through `viewsForUrl`
   *  because the two schedule routes each carry a `rooms`, so a workspace-wide
   *  id lookup would be ambiguous. `queryParamsHandling: 'merge'` keeps the
   *  rest of the schedule's URL state (date, filters) intact; a `null` value
   *  clears that one param. */
  /**
   * THE PRESS TRACE, at most one at a time.
   *
   * A list rather than a nullable, because `@for` with a `track` is what
   * remounts the layer when a second pill is pressed inside the first one's
   * 900ms. An `@if` over a replaced object updates the inputs of the element
   * already there — and the animation, which is running off the stylesheet,
   * would not restart. The reader would press Stream and see nothing.
   */
  protected readonly navTraces = signal<readonly NavTrace[]>([]);
  private navTraceKey = 0;
  private navTraceTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Draw the trace over the pill that was just pressed.
   *
   * Reads the tab's rectangle at press time, which is the one moment it is
   * guaranteed to be laid out and on screen. Silent when there is nothing to
   * draw — a text-only view (Schedule's are), a pill in the overflow, or a
   * Paper release that renamed the class `findViewIconRect` looks for. Losing
   * an animation is the correct failure for all three.
   */
  private traceNavPress(view: PaperNavView): void {
    if (!view.icon) return;
    const rect = findViewIconRect(this.document, view.id);
    if (!rect) return;
    if (this.navTraceTimer) clearTimeout(this.navTraceTimer);
    this.navTraces.set([{ key: ++this.navTraceKey, icon: view.icon, rect }]);
    this.navTraceTimer = setTimeout(() => {
      this.navTraceTimer = null;
      this.navTraces.set([]);
    }, NAV_TRACE_MS);
  }

  protected onNavViewSelected(view: PaperNavView): void {
    // Feedback first: the navigation below can replace the whole surface, and
    // a confirmation that lands after the thing it confirms is a flicker.
    this.traceNavPress(view);
    // A context's views win over the workspace's inside Paper, so they win
    // here too. A community's are `?tab=` states of ONE route whose path
    // carries the id — hence the registry helper rather than a literal.
    const communityId = this.communityContextId();
    if (communityId) {
      const { commands, queryParams } = communityViewCommands(communityId, view.id);
      void this.router.navigate([...commands], {
        queryParams,
        queryParamsHandling: 'merge',
        // A tab is a view of the same place, not a new one. Pushing would
        // fill the back-stack with entries all labelled with the same
        // community, so back would step through tabs instead of leaving.
        replaceUrl: true,
      });
      return;
    }
    // The Studio's five views are five ROUTES, each with its own lazily
    // loaded surface, so the destination is a plain URL — resolved under the
    // root the person is actually on, which is what keeps a click inside
    // `/preview/content` inside `/preview/content` instead of throwing a
    // reviewer into a guarded route they have no session for.
    //
    // PUSHED, NOT REPLACED, and the community branch above is not a precedent
    // for the opposite. A community tab is a `?tab=` state of ONE route, so
    // pushing would fill the stack with entries all naming the same
    // community; a Studio section is a different route with a different
    // surface and its own `section-opened` event. Back retracing the sections
    // you actually visited is what the rail did, and moving the control into
    // the bar is not a reason to change what pressing Back means.
    if (this.inContentStudio()) {
      const target = studioNavTarget(this.activeUrl() ?? '', view.id);
      // Null means the view has no destination on THIS mount — curation on the
      // preview harness. The track does not offer it there, so this is only
      // reachable from a stale emission or a positional shortcut; navigating
      // to a fabricated URL would be the defect, not the guard.
      if (target !== null) void this.router.navigateByUrl(target);
      return;
    }
    const item = viewsForUrl(this.activeWorkspaceItem(), this.activeUrl() ?? '').find(
      candidate => candidate.id === view.id,
    );
    if (!item) return;
    if (item.queryParams) {
      void this.router.navigate([item.route], {
        queryParams: item.queryParams,
        queryParamsHandling: 'merge',
      });
      return;
    }
    void this.router.navigateByUrl(item.route);
  }

  /** The tenant lockup — the brand cap's go-home action, which used to be
   *  `im-org-switcher`'s logo link. The organism reports nothing for it, so
   *  this is the one nav surface the shell still instruments itself. */
  protected onLockupSelected(): void {
    const destination = workspaceRoute('home');
    this.analytics.capture(ANALYTICS_EVENTS.SHELL_LOCKUP_SELECTED, {
      from_section: this.shell.currentSection() ?? 'home',
      destination,
    });
    void this.router.navigateByUrl(destination);
  }

  /** `?` on the bar, or the workspace menu's Shortcuts row. The dialog fires
   *  SHELL_SHORTCUTS_DIALOG_OPENED itself on open — nothing to add here. */
  protected onShortcutsRequested(): void {
    this.shell.openShortcutsDialog('direct');
  }

  /**
   * BACK — the crumb's arrow and the bar's own `Alt + ←`. Both arrive here,
   * and both delegate to the browser through `NavHistoryService`, so they
   * and the browser's own button are one affordance rather than three that
   * drift apart.
   *
   * `back()` returns false when it did not move: either the stack is empty
   * (a cold link) or the current surface owns its exit — the article editor
   * registers a guard so a generic back runs `requestExit()`, flushing a
   * final save and raising "Leave without saving?" rather than routing
   * around the best unsaved-work handling in the app.
   *
   * Paper reports the crumb's arrow and its own shortcut through
   * PAPER_ANALYTICS, so nothing is captured here.
   */
  protected onBackRequested(): void {
    this.navHistoryService.back();
  }

  /**
   * UP — the crumb's root row. A fixed destination regardless of route,
   * which is exactly what a cold link needs and what back cannot be.
   */
  protected onContextRootSelected(): void {
    const context = this.navContext();
    if (context?.kind !== 'community') return;
    this.analytics.capture(ANALYTICS_EVENTS.SHELL_CONTEXT_UP_SELECTED, {
      context_kind: context.kind,
      context_id: context.id,
      destination: COMMUNITY_ROOT_ROUTE,
      had_back: this.navHistory().length > 0,
    });
    void this.router.navigateByUrl(COMMUNITY_ROOT_ROUTE);
  }

  /**
   * ACROSS — a peer picked in the sibling switcher. §7.3's payoff: moving
   * between communities was back → browse → find → click, and is now two
   * clicks from anywhere inside one. Worth its own event for that reason —
   * Paper reports that the switcher was used, not that the journey it
   * replaces got shorter.
   */
  protected onSiblingSelected(sibling: PaperNavContextSibling): void {
    const context = this.navContext();
    if (context?.kind !== 'community') return;
    if (sibling.id === context.id) return;
    this.analytics.capture(ANALYTICS_EVENTS.SHELL_CONTEXT_SIBLING_SELECTED, {
      context_kind: context.kind,
      from_context_id: context.id,
      to_context_id: sibling.id,
      sibling_count: context.siblings.length,
    });
    // A peer opens at its own default tab, never carrying this one's over —
    // `?tab=links` means nothing in a community with no links.
    const { commands } = communityViewCommands(sibling.id, COMMUNITY_DEFAULT_VIEW);
    void this.router.navigate([...commands]);
  }

  protected onBottomNavSelected(key: string): void {
    this.analytics.capture(ANALYTICS_EVENTS.BOTTOM_NAV_SECTION_SELECTED, {
      section: key,
      from_section: this.shell.currentSection(),
    });
    // Settings now resolves to the admin shell (/admin) for admins and to
    // personal settings (/profile) for everyone else — see settingsDestination.
    if (key === 'settings') {
      this.router.navigateByUrl(this.settingsDestination());
      return;
    }
    const workspace = findWorkspace(key);
    if (workspace) {
      this.router.navigate([workspace.route]);
    }
  }
}
